/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion, AnimatePresence } from 'motion/react';
import { 
  Network, 
  ShieldCheck, 
  Cpu, 
  Globe, 
  Mail, 
  Phone, 
  MapPin, 
  ExternalLink, 
  Award, 
  Monitor,
  RefreshCw,
  Sun,
  Moon,
  Briefcase,
  BookOpen,
  Settings,
  GraduationCap, 
  Users,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Server,
  Zap,
  Sparkles,
  CheckCircle2,
  Layers,
  Wifi,
  Activity,
  Camera,
  Music,
  Heart,
  Quote,
  Home,
  User,
  FileText,
  MessageSquare,
  Search,
  ArrowUp,
  ArrowRight,
  CreditCard,
  Cookie,
  Palette,
  Wrench,
  DollarSign,
  Utensils,
  Lock,
  Calendar,
  Building2,
  Laptop,
  Calculator
} from 'lucide-react';
import React, { useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from './lib/firebase';
const portraitImage = "/uploads/portrait.png";
const headshotImage = "/uploads/Randon_Brown_Head.png";
import WeatherWidget from './components/WeatherWidget';
import { SiteSearch } from './components/SiteSearch';
import BrandLogo from './components/BrandLogo';
import { CookiePolicyModal, useCookieConsent } from './components/CookieConsent';
import NavigationHub from './components/NavigationHub';
import { WebsiteOnboardingGuide } from './components/WebsiteOnboardingGuide';
import { ServiceQuoteRequest } from './components/ServiceQuoteRequest';
import { PricingPage } from './components/PricingPage';
import { PriceEstimatorPage } from './components/PriceEstimatorPage';
import { TermsAndConditions } from './components/TermsAndConditions';
import { TermsAndConditionsModal } from './components/TermsAndConditionsModal';
import { PrivacyPolicyModal } from './components/PrivacyPolicyModal';
import { AdminDashboard } from './components/AdminDashboard';
import { ClientPortal } from './components/ClientPortal';
import { BooksShowcase } from './components/BooksShowcase';
import { BottomCallToAction } from './components/BottomCallToAction';

const LinkedInIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    aria-hidden="true"
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const YouTubeIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    aria-hidden="true"
  >
    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
    <polygon points="10 15 15 12 10 9 10 15" fill="currentColor" stroke="none" />
  </svg>
);

const EXPERIENCE = [
  {
    company: "Freelance Technical Services",
    location: "Regional",
    role: "Web & Logo Designer / IT Consultant",
    period: "2010 - Present",
    achievements: [
      "Crafted custom logo designs, brand visual identities, and high-performance websites for businesses and private clients.",
      "Provided strategic technology consulting, systems integration, and infrastructure support.",
      "Managed full lifecycle of design and web projects, from vector graphics and typography to hosting, DNS, and modern UI/UX."
    ]
  },
  {
    company: "Oak Hill R-1 School District",
    location: "Salem, MO",
    role: "Director of Technology",
    period: "2014",
    achievements: [
      "Successfully rebuilt network infrastructure from the ground up.",
      "Implemented Active Directory and Google Apps for Education.",
      "Properly documented all aspects of network functionality.",
      "Installed a cloud-based web filter for CIPA compliance.",
      "Implemented LanSchool for computer management and ITALC.",
      "Cleaned up wiring where necessary."
    ]
  },
  {
    company: "Cabool R-IV School District",
    location: "Cabool, MO",
    role: "Director of Technology",
    period: "2015 - 2020",
    achievements: [
      "Modernized district-wide network infrastructure, including high-density wireless deployment.",
      "Implemented enterprise-level security protocols, DNS filtering, and SSL decryption.",
      "Managed IT budget and long-term technology roadmaps to align with educational goals.",
      "Integrated Google Workspace and Clever SSO for seamless student/staff access."
    ]
  },
  {
    company: "City of Houston",
    location: "Houston, MO",
    role: "Director of Technology / Network Broadband Specialist",
    period: "2020 - 2025",
    achievements: [
      "Managed the implementation of a $3M+ Fiber-to-the-Home (FTTH) internet system servicing 1,200 potential customers.",
      "Oversaw critical municipal infrastructure including automated metering and law enforcement systems.",
      "Drafted municipal broadband ordinances and city-wide technology usage policies.",
      "Implemented Cisco Meraki Firewalls and RMM capabilities at scale.",
      "Maintained Microsoft Windows Server 2012-2022 and Active Directory infrastructure."
    ]
  },
  {
    company: "CHC Do-It Center",
    location: "Rogersville, MO",
    role: "Information Systems",
    period: "May 2025 – Aug 2025",
    achievements: [
      "Engineered and installed a 27-camera security system for the Rogersville location.",
      "Deployed RMM agents and centralized endpoint control to streamline tech support.",
      "Reduced yearly technology expenditures through strategic vendor analysis."
    ]
  },
  {
    company: "Crocker Consulting Engineers, Inc.",
    location: "Springfield, MO",
    role: "Website Developer",
    period: "Nov 2025 – Present",
    achievements: [
      "Executing a full responsive redesign of the company website to modernize their online presence.",
      "Implementing modern UI/UX principles for institutional engineering services."
    ]
  },
  {
    company: "Licking R-8 School District",
    location: "Licking, MO",
    role: "IT Technician (Sub-Tech)",
    period: "Jan 2026 – Present",
    achievements: [
      "Assists in maintaining and repairing technology systems for a district of 800+ students.",
      "Specializes in Chromebook hardware repair and staff technical support."
    ]
  }
];

const SKILLS = [
  { 
    category: "Infrastructure & Networking", 
    items: ["FTTH (GPON/OLT)", "Nokia 7750", "AMS 5520", "Nokia 7360 OLT", "VLAN Configuration & Management", "LAG Configuration", "Ubiquiti Edge Router", "Layer 2/3 Switching", "VPN Technologies", "Ubiquiti (PTP/UniFi)", "Wi-Fi Standards (802.11a/b/g/n/ac/ax/be)", "HP Aruba", "Cisco Meraki", "Wireless Mesh Systems", "CAT 3/5/6/7 Wiring", "Starlink Internet Installation", "Anker Solix Systems", "UPS (APC, Tripp Lite, etc.)", "Intercom Systems", "Grandstream Phones", "Tadiran Phones", "Dual WAN Fail over Systems"] 
  },
  { 
    category: "Virtualization & OS", 
    items: ["Windows (DOS - 11)", "Windows Server 2003 - Present", "VMware", "Hyper-V", "Proxmox VE", "Docker", "Portainer", "VirtualBox", "Clonezilla", "Linux (Ubuntu, Mint, RHEL, Fedora)", "Debian", "TV Systems (Samsung, Roku, etc.)", "Android/iOS", "Chromebooks", "NVMe, M.2 SATA, SSD, HDD Provisioning"] 
  },
  { 
    category: "Admin & Security Suites", 
    items: ["Microsoft 365 & Apps", "Google Workspace & Apps", "Active Directory", "Group Policy", "Let's Encrypt", "Cloudflare", "Cloudflare Security", "Wayback Machine", "Fog Imaging", "Chrome Recovery Environment", "Smoothwall Firewall", "CIPAFilter", "OpenDNS", "ControlD", "Veeam", "Synology Backup & Recovery", "Synology C2 Identity", "Sophos", "Bitdefender Gravityzone", "Windows Defender", "Avast", "McAfee", "RMM (Action 1, PDQ, Smart Deploy)", "Ticketing Systems (Spiceworks, Sonar B/OSS)", "LanSchool", "Securly", "TightVNC", "WinSCP"] 
  },
  { 
    category: "Cloud, Web & Specialized", 
    items: ["Amazon AWS", "Google Cloud", "Digital Ocean", "Cloudflare Workers/Pages", "Nextcloud", "WordPress", "Wix Site Builder", "Webs.com", "GoDaddy", "Video Game Emulators", "IPTV", "Web Design & Maintenance", "Logo Design & Branding", "Digital Signage", "MULES Law Enforcement", "ADF Forensics", "Google AI Studio", "Gemini", "Claude", "Grok", "Co-Pilot", "ChatGPT", "AI Models", "Vibe Coding", "Canva", "Adobe Express", "Web Scrapers", "Paxton Access"] 
  },
  {
    category: "Physical Security",
    items: ["Video Insights", "Panasonic Security Cameras", "Axis Security Cameras", "Eufy Security Cameras", "Swann Security Cameras", "Night Owl Security Cameras", "Genetec Security", "Rhombus Camera Systems", "Reolink Security Cameras"]
  }
];

const SKILL_DESCRIPTIONS: Record<string, string> = {
  // Infrastructure & Networking
  "FTTH (GPON/OLT)": "Fiber to the Home technology utilizing Gigabit Passive Optical Networks for high-performance Gigabit delivery.",
  "Nokia 7750": "Carrier-grade multi-service router designed for high-capacity, high-availability IP network routing.",
  "AMS 5520": "Nokia Access Management System used for configuring and monitoring municipal and enterprise broadband systems.",
  "Nokia 7360 OLT": "High-capacity network access node deployed to deliver ultra-broadband fiber-optic services to endpoints.",
  "VLAN Configuration & Management": "Virtual Local Area Networks configured to segment physical networks, optimizing broadcast domains and security.",
  "LAG Configuration": "Link Aggregation Groups combining multiple physical Ethernet links into a single, high-bandwidth logical connection.",
  "Ubiquiti Edge Router": "Enterprise-grade router platforms offering advanced protocols, firewalls, and detailed CLI routing configurations.",
  "Layer 2/3 Switching": "Hardware-based Ethernet packet switching and IP-routing to construct secure, high-speed campus networks.",
  "VPN Technologies": "Virtual Private Network protocols (IPsec, OpenVPN, WireGuard) enabling encrypted site-to-site and remote client bridges.",
  "Ubiquiti (PTP/UniFi)": "Wireless Point-to-Point backhauls and centralized management systems for enterprise-grade wireless networks.",
  "Wi-Fi Standards (802.11a/b/g/n/ac/ax/be)": "Expertise in standard wireless protocols, managing channels and frequencies from legacy bands to Wi-Fi 7.",
  "HP Aruba": "Enterprise campus network switches and access systems with advanced VLAN and edge policy administration.",
  "Cisco Meraki": "Cloud-managed IT infrastructure platform for centralized dashboard control of enterprise switches, firewalls, and wireless access points.",
  "Wireless Mesh Systems": "Dynamic wireless topology implementing multiple interconnected access points to extend signal without wired backhauls.",
  "CAT 3/5/6/7 Wiring": "Physical copper media cabling engineering, including structured system runs, terminations, and active signal verification.",
  "Starlink Internet Installation": "Satellite receiver alignment, secure mounting hardware, custom cabling paths, and integration into existing networks.",
  "Anker Solix Systems": "Smart energy storage, solar power backups, and portable power station configuration and integration.",
  "UPS (APC, Tripp Lite, etc.)": "Uninterruptible Power Supply systems configuration, battery maintenance, network management card setup, and load monitoring.",
  "Intercom Systems": "Hardwired and IP-based voice/video entry communication equipment for campus safety and announcements.",
  "Grandstream Phones": "SIP/VoIP endpoints, automated template provisioning, trunk integrations, and dedicated audio QoS.",
  "Tadiran Phones": "Legacy PBX telephone system configurations, programming, analog station runs, and trunk routing.",

  // Virtualization & OS
  "Windows (DOS - 11)": "Deep expertise across legacy command-line and modern desktop Microsoft operating system configurations.",
  "Windows Server 2003 - Present": "Server administration, domain integration, directory permissions, group shares, and system patches.",
  "VMware": "Type-1 hypervisor for deploying and configuring resource-efficient, isolated virtual machines.",
  "Hyper-V": "Microsoft's hypervisor system for virtualization, storage replication, and server system scaling.",
  "Proxmox VE": "Open-source hypervisor environment for managing virtual servers and LXC container environments.",
  "Docker": "Lightweight containerization engine used to package applications in isolated, highly reproducible runtimes.",
  "Portainer": "Web-based container management platform for deploying, configuring, and monitoring Docker container environments and clusters.",
  "Debian": "Highly stable, secure open-source Linux operating system used to host robust server systems, databases, and core services.",
  "VirtualBox": "Type-2 virtualization software used to test sandboxed operating systems locally on workstations.",
  "Clonezilla": "Bare-metal imaging and partition cloning utility for deploying massive operating systems across workstations.",
  "Linux (Ubuntu, Mint, RHEL, Fedora)": "Command-line terminal operations, repository updates, bash scripting, and secure network services.",
  "TV Systems (Samsung, Roku, etc.)": "Enterprise smart television setups, content scheduling engines, and automated display layouts.",
  "Android/iOS": "Mobile OS ecosystem setup, MDM deployment, application profiles, and active security compliance policies.",
  "Chromebooks": "Enterprise deployment, ChromeOS administration, policy management, Google Admin console enrollment, and hardware servicing.",
  "NVMe, M.2 SATA, SSD, HDD Provisioning": "Configuring, partitioning, imaging, and upgrading high-speed solid-state drives and traditional magnetic media storage setups.",

  // Admin & Security Suites
  "Microsoft 365 & Apps": "Cloud suite management containing Exchange, SharePoint, custom routing, and licensed user policies.",
  "Google Workspace & Apps": "Organizational administration, Gmail domain configurations, shared drives, and user security keys.",
  "Active Directory": "Centralized network database mapping user accounts, computer assets, and security access tokens.",
  "Group Policy": "Windows server system policies to enforce registry settings and security baselines across enterprise endpoints.",
  "Let's Encrypt": "Automated security authority for creating and updating valid SSL/TLS certificates for web platforms.",
  "Cloudflare": "Global content delivery network (CDN), high-performance DNS routing, and enterprise DDoS defenses.",
  "Cloudflare Security": "WAF configuration, DDoS protection, rate limiting, secure access controls, and zero-trust security integrations.",
  "Wayback Machine": "Digital web archive searches used to investigate legacy domains, changes, and historical configurations.",
  "Fog Imaging": "Network-based computer imaging solution for deploying massive operating system clones concurrently.",
  "Chrome Recovery Environment": "Dedicated recovery framework to restore Chromebook systems to native, secure boot conditions.",
  "Smoothwall Firewall": "Advanced firewalls featuring deep web filtering and traffic prioritization for school systems.",
  "CIPAFilter": "Specialized network firewall and web-content filter designed to ensure complete school district CIPA compliance.",
  "OpenDNS": "Cloud-delivered internet security filtering network-level requests prior to IP connection.",
  "ControlD": "Next-gen DNS manager enabling custom filters, ad blockers, and global geolocation steering.",
  "Veeam": "Enterprise backup and recovery engine for securing virtual machine clusters and physical data servers.",
  "Synology Backup & Recovery": "Network-attached storage (NAS) array configurations, custom volume raid states, and secure cloud pipelines.",
  "Synology C2 Identity": "Cloud identity solution for secure directory services, single-sign-on (SSO), and access control.",
  "Sophos": "Threat intelligence platforms and endpoint antivirus solutions for servers and workstation fleets.",
  "Bitdefender Gravityzone": "Unified security agent delivering behavior analysis, active anti-ransomware, and cloud-managed scans.",
  "Windows Defender": "Microsoft's endpoint detection and security service configured for threat scanning and behavior analysis.",
  "Avast": "Endpoint threat monitoring software configured to guard client systems against web exploits and bad files.",
  "McAfee": "Network security suite protecting endpoints and legacy servers against digital exploits.",
  "RMM (Action 1, PDQ, Smart Deploy)": "Remote monitoring tools automating silent software installations, system inventory, and critical patch updates.",
  "Ticketing Systems (Spiceworks, Sonar B/OSS)": "IT helpdesk, incident ticketing, customer support workflows, and automated service management/billing architectures.",
  "LanSchool": "Classroom management and device monitoring software for real-time student screen visibility, digital guidance, and web limiting.",
  "Securly": "Cloud-based web filtering, student safety monitoring, and device management platform designed for educational institutions.",
  "TightVNC": "Lightweight remote desktop sharing software for remote control, technical troubleshooting, and remote client administration.",
  "WinSCP": "Secure SFTP, FTP, WebDAV, and SCP client for Windows used to securely transfer files between local workstations and remote servers.",

  // Cloud, Web & Specialized
  "Amazon AWS": "Cloud infrastructure tools including elastic virtual servers (EC2) and redundant block storage (S3).",
  "Google Cloud": "Cloud network configurations, application engine instances, and serverless API integration endpoints.",
  "Digital Ocean": "Lightweight virtual private servers (Droplets), volume storage, and database setups.",
  "Cloudflare Workers/Pages": "Serverless edge computing and lightning-fast static hosting for modern web systems.",
  "WordPress": "Full-stack content management, custom database administration, plugins, templates, and server security.",
  "Wix Site Builder": "Rapid web layout design, custom domain routing, visual layouts, and content management systems.",
  "Webs.com": "Legacy browser-based hosting platform, database structures, and platform migrations.",
  "GoDaddy": "Domain registrar settings, DNS record routing, SSL activations, and traditional email hosting servers.",
  "Video Game Emulators": "Digital systems mapping hardware components, input devices, and software code libraries for retro archives.",
  "IPTV": "IP-based video systems, audio/video streaming streams, custom decoder box routing, and media transport.",
  "Web Design & Maintenance": "Frontend code styling, layout optimizations, and continuous updates to corporate platforms.",
  "Logo Design & Branding": "Designing distinctive vector logos, typography hierarchies, brand identity marks, color systems, and corporate asset packages for print and digital channels.",
  "Digital Signage": "Server-client video screens programmed to display dynamic schedules and corporate media feeds.",
  "MULES Law Enforcement": "Missouri Law Enforcement system configuration, secure access terminals, and private network routing.",
  "ADF Forensics": "Digital forensics imaging and intelligent triage software used to analyze and gather evidence from storage media.",
  "Google AI Studio": "Prototyping generative workflows, crafting model system instructions, API key management, and prompt engineering with Gemini.",
  "Gemini": "Google's advanced multimodal artificial intelligence models for problem solving, code writing, and automated system logic.",
  "Co-Pilot": "Microsoft's AI assistant integrated into enterprise productivity tools and development IDEs to speed up engineering and writing workflows.",
  "ChatGPT": "OpenAI's conversational generative pre-trained transformer model used to automate assistance, generate code, and answer technical queries.",
  "AI Models": "Locally hosted neural networks, smart query prompts, and active model interface integrations.",
  "Vibe Coding": "Advanced AI-assisted engineering allowing rapid development of complex web applications.",
  "Paxton Access": "Electronic security card access nodes, magnetic locking hardware, and central server user control.",
  "Nextcloud": "Self-hosted productivity platform offering secure cloud storage, file sharing, calendar syncing, and collaborative office suites.",
  "Claude": "Anthropic's advanced generative AI models integrated for automated code writing, documentation, and logic workflows.",
  "Grok": "xAI's conversational artificial intelligence engine used for real-time information retrieval, technical analysis, and code generation.",
  "Dual WAN Fail over Systems": "High-availability network setups providing redundant internet connections with automated hot-failover and path recovery.",
  "Canva": "Visual design platform used to produce high-impact graphics, marketing assets, layouts, and public-facing documents.",
  "Adobe Express": "Cloud-based graphic and video creation tools used to design professional layouts, promotional materials, and web-ready assets.",
  "Web Scrapers": "Custom automated data extraction tools and scripts designed to harvest, parse, and structure web information.",

  // Physical Security
  "Video Insights": "Enterprise-grade digital video management platform for integrating multi-site security cameras.",
  "Panasonic Security Cameras": "Professional surveillance cameras engineered with high-definition optical lenses and IP enclosures.",
  "Axis Security Cameras": "High-end network security cameras incorporating intelligent on-device video analytics and custom streams.",
  "Eufy Security Cameras": "Smart wireless cameras featuring long-life battery operations and local secure hubs.",
  "Swann Security Cameras": "Coaxial and network video recorder surveillance systems with offline capture capabilities.",
  "Night Owl Security Cameras": "Continuous-recording analog security systems for night-vision and multi-channel safety.",
  "Genetec Security": "Unified security solution uniting video, active access control, and dynamic security intelligence.",
  "Rhombus Camera Systems": "Next-gen cloud-managed surveillance cameras incorporating intelligent AI event classification and triggers.",
  "Reolink Security Cameras": "Surveillance cameras with high-definition IP streams, local NVR integration, and smart person/vehicle detection."
};

const INTERESTS = [
  { label: "Hobbies", desc: "Dedicated family man and passionate technologist.", icon: <Users /> },
  { label: "Technical", desc: "Common sense approaches to complex technical challenges.", icon: <Cpu /> },
  { label: "Creative Writing", desc: "Published children's book author inspired by his family. Creator of 'A Boy and His Brother'.", icon: <BookOpen />, link: "https://www.amazon.com/dp/B0H73PXHB3?lv" },
  { label: "Music Collaboration", desc: "Co-created a beautiful album with his wife titled 'Silence Speaks'. Available to stream.", icon: <Music />, link: "https://music.apple.com/us/album/silence-speaks/1783635583?i=1783635816" },
  { label: "Outdoor Adventurist", desc: "Randon enjoys freshwater catfish fishing, firearms collecting, maintaining a well-manicured lawn, and swimming. Outside of tech, he loves spending time with his beautiful wife, three amazing children, and teaching his cat how to box (yes, really)!", icon: <Globe /> },
  { label: "Culinary", desc: "Randon loves food, so much so, that he attempts to crack restaurant recipes and even made his own cookbook called \"Ran-Delicious\"! You can trust this skinny chef!", icon: <Utensils /> }
];

interface Stat {
  label: string;
  value: string;
  icon: ReactNode;
  category: string;
  subLabel?: string;
}

const STATS: Stat[] = [
  { label: "Project Value", value: "$3M+", category: "Scale", icon: <Zap className="w-5 h-5" /> },
  { label: "Miles of Wire", value: "18+", category: "Infrastructure", icon: <Network className="w-5 h-5" /> },
  { label: "Residents Impacted", value: "25,000+", category: "Scale", icon: <Users className="w-5 h-5" /> },
  { label: "Tickets Solved", value: "10,000+", category: "Operations", icon: <CheckCircle className="w-5 h-5" /> },
  { label: "Students Served", value: "1,000+", category: "Experience", icon: <GraduationCap className="w-5 h-5" /> },
  { label: "Systems Upgraded", value: "1,000+", category: "Operations", icon: <Monitor className="w-5 h-5" /> },
  { label: "Staff Supported", value: "200+", category: "Operations", icon: <Cpu className="w-5 h-5" /> },
  { label: "Cameras Deployed", value: "100+", category: "Infrastructure", icon: <ShieldCheck className="w-5 h-5" /> },
  { label: "System Uptime", value: "99%", category: "Operations", subLabel: "* Tracked average", icon: <RefreshCw className="w-5 h-5" /> },
  { label: "Switches Installed", value: "50+", category: "Infrastructure", icon: <Layers className="w-5 h-5" /> },
  { label: "Servers Managed", value: "20+", category: "Infrastructure", icon: <Server className="w-5 h-5" /> },
  { label: "Years Leading", value: "11+", category: "Experience", icon: <Briefcase className="w-5 h-5" /> }
];

const RECOMMENDATIONS = [
  {
    name: "Troy Terrillion",
    role: "Owner - Waymark Consulting",
    org: "Houston, MO",
    excerpt: "Randon is top notch.",
    full: "Randon is top notch."
  },
  {
    name: "Louis Iglesias",
    role: "Outside Sales Executive",
    org: "SCS Telecom Supply",
    excerpt: "He’s extremely organized and efficient... You never have to wonder where things stand when he’s involved. Randon played an essential role in bringing fiber-to-the-home service to Houston.",
    full: "I had the chance to work with Randon at the City of Houston, and he was someone you could always count on. He’s extremely organized and efficient, and it shows in the way he handles projects. Randon keeps things moving without overcomplicating them, and he follows through on what he starts. You never have to wonder where things stand when he’s involved. He’s also just genuinely easy to work with. He communicates clearly, works well with others, and brings a reliable presence to the team. Randon played an essential role in bringing fiber-to-the-home service to Houston, Missouri, which was a huge step forward for the community."
  },
  {
    name: "Robert Mortensen",
    role: "Director of Parks and Recreation",
    org: "City of Houston, MO",
    excerpt: "Randon demonstrated exceptional leadership, technical expertise, and a strong work ethic. His ability to troubleshoot complex problems made him an invaluable asset.",
    full: "I am pleased to write this letter of recommendation for Randon Brown, who served as the IT and Fiber Director at the City of Houston. During his time working with us, Randon demonstrated exceptional leadership, technical expertise, and a strong work ethic. As the IT and Fiber Director, he was responsible for managing and overseeing the municipality's IT infrastructure, including our fiber optic network. Randon consistently delivered results that improved our system's reliability and efficiency. His ability to troubleshoot complex problems, implement new technologies, and ensure seamless communication between departments made him an invaluable asset to our team. Beyond his technical skills, Randon is an excellent communicator and collaborator. His blend of technical proficiency, leadership, and interpersonal skills would make him a tremendous asset to any organization."
  },
  {
    name: "James Lightfoot",
    role: "President",
    org: "ACRS 2000 Corp.",
    excerpt: "Randon exhibits a strong work ethic and coupled with his vast technical knowledge, is the key to the City of Houston, MO's successful fiber-to-the-home project.",
    full: "I have worked with Randon for the past couple of years and have enjoyed the personal and business relationship. Randon exhibits a strong work ethic and coupled with his vast technical knowledge, is the key to the City of Houston, MO's successful fiber-to-the-home project."
  },
  {
    name: "Joshua Edwards",
    role: "IT Professional",
    org: "Cabool R-IV School District",
    excerpt: "I saw his attention to detail, customer service and network skills were impeccable! He is an IT that will take any company to its max potential when it comes to technology!",
    full: "Randon is a highly skilled IT Professional who I had the pleasure to work with at Cabool R-IV School District. During this time, I saw his attention to detail, customer service and network skills were impeccable! He thinks outside of the box, resolving each issue with sheer pride and determination. He is an IT that will take any company to its max potential when it comes to technology! It would be an honor to work with Randon on any project or business venture in the future!"
  },
  {
    name: "Aaron Miller",
    role: "Elementary Principal",
    org: "Cabool R-IV School District",
    excerpt: "Randon has been a vital part of the growth and expansion... I always had great conversations with Randon and never questioned his integrity or morals.",
    full: "Randon has been a vital part of the growth and expansion of the Cabool Elementary technology expansion as we have sought to grow our access to technology and the skills with the technology for our upcoming generations. The upgrading and implementing of better quality tools for staff on a small school budget has also been a major part of my work with Randon. During these years I have seen Randon grow as he interacted with teachers and learned how to work more effectively with staff and the time constraints we all endure. I always had great conversations with Randon and never questioned his integrity or morals. He shows a great passion for technology and doing his job each day."
  },
  {
    name: "Doug Sutton",
    role: "IT/Fiber Technician",
    org: "City of Houston, MO",
    excerpt: "Randon is an exceptionally skilled IT professional with a deep understanding of infrastructure. He is a forward thinker, always looking for innovation.",
    full: "Randon is an exceptionally skilled IT professional with a deep understanding of the complexities surrounding modern technology infrastructure. Whether overseeing the design and implementation of fiber-optic networks, troubleshooting network issues, or providing strategic insights into technological advancements, Randon consistently goes above and beyond in his role. As an IT Director, Randon’s leadership and vision have been instrumental in ensuring the stability and scalability of our network systems. He has an excellent ability to manage projects and communicate technical concepts to both technical and non-technical stakeholders. Randon is dependable, responsive, and always approaches challenges with a solution-oriented mindset."
  },
  {
    name: "Jason VonAllmen",
    role: "Teacher",
    org: "Cabool R-IV School District",
    excerpt: "He is honest, dependable, and incredibly hard-working. He was always thorough and timely, and had a great rapport with school faculty.",
    full: "Mr. Brown and I have been coworkers at the Cabool RIV School District for 5 years. I thoroughly enjoyed my time working with Mr. Brown, and came to know him as a truly valuable asset to absolutely any team. He is honest, dependable, and incredibly hard-working. He worked within several buildings serving technological support. He was always thorough and timely, and in addition to solving technical issues he had a great rapport with school faculty. Without a doubt, I confidently recommend Randon Brown to join your team."
  },
  {
    name: "Sherry Wheat",
    role: "Teacher / Grant Coordinator",
    org: "Cabool R-IV School District",
    excerpt: "Randon not only organized the wiring within the district... He has always been pleasant and patient, dealing with staff of variety levels of knowledge.",
    full: "During the time Randon has been at the school, he has worked alone and in conjunction with an assistant to maintain and upgrade the various forms of technology within the district. He has been exceedingly challenging, considering he has been working in buildings built during the 1930's. Randon designed a 'ticket' system within the district, to enable staff to communicate electronically with his department. I personally have benefited from Randon's expertise while a teacher with the district. Randon not only organized the wiring within the district, but also in individual classrooms, eliminating trip hazards and creating a more aesthetic appearance. He also provided invaluable assistance with selection of technology for a grant program I participated in."
  },
  {
    name: "Mike Magers",
    role: "Senior Engineer",
    org: "M&M Telecom",
    excerpt: "I can personally attest to Randon's patience in dealing with issues that would have frustrated most other technicians. He was always deliberate.",
    full: "I have had the opportunity to work with Randon Brown, as the IT director for Cabool R-IV School District since 2015. In those five years, Randon has demonstrated numerous times his professionalism, as well as his resourcefulness in facing issues and working through problems to a successful conclusion. I can personally attest to Randon's patience in dealing with issues that would have frustrated most other technicians. It was always a pleasure to work with Randon in solving problems or trouble-shooting issues since he was always deliberate and followed the rules of trouble shooting that many customers don't seem to grasp."
  }
];

const AI_APPS = [
  {
    name: "Fridge Feasts",
    url: "https://fridge-feasts.pages.dev",
    description: "AI-powered recipe generator that helps you cook amazing meals using only what's in your fridge.",
    features: ["Gemini Vision Integration", "Smart Substitutions", "Dietary Filtering"],
    icon: <Zap className="w-6 h-6" />
  },
  {
    name: "Ultimate Bible App",
    url: "https://ultimatebibleappprod.pages.dev/",
    description: "A transformative reading experience enhanced by AI to explore scripture in deep, meaningful ways.",
    features: ["Contextual Insights", "Cross-Reference Engine", "Modern UX"],
    icon: <Globe className="w-6 h-6" />
  },
  {
    name: "Pro-Drag Racing Simulator",
    url: "https://pro-drag.pages.dev/",
    description: "Randon's high-fidelity drag racing simulator, showcasing physics modeling and response analytics.",
    features: ["Physics Simulation", "Reaction Time Analytics", "Real-Time Feedback"],
    icon: <Activity className="w-6 h-6" />
  }
];

const SHOWCASE_PROJECTS = [
  {
    name: "Built a $3M+ Municipal Fiber Network",
    category: "Flagship Infrastructure",
    description: "Directed the end-to-end implementation of a 1 Gbps Fiber to the Home (FTTH) ISP system for the City of Houston. This involved high-level strategic planning, vendor management, and engineering oversight to serve over 1,200 potential customers.",
    stats: [
      { value: "1,200", label: "Potential Clients" },
      { value: "1 Gbps", label: "Fiber Speeds" }
    ],
    links: [
      { text: "Worked with over 60 network engineers on designing and building Texas County, Missouri's first municipal fiber to the home Internet Service Provider." },
      { text: "Successfully maintained control of 30 percent of the town's take-rate." },
      { text: "Oversaw professional contracts, informed board members, and maintained fiscal responsibility of tax-payer dollars." },
      { text: "Deployed automated billing & payment portals for customer service and billing." },
      { text: "Ensured top-notch customer satisfaction with a limited staff." },
      { text: "Robert Williams Systems Achievement Award (2021)", url: "https://houstonherald.com/2021/11/houston-honored-by-utility-organization/" },
      { text: "Featured in Community Networks Publication (2024)", url: "https://communitynets.org/content/houston-missouris-municipal-fiber-network-revs-citys-economic-development-engine-big-city" }
    ],
    isFlagship: true
  },
  {
    name: "City of Houston, MO Municipal IT Infrastructure Overhaul",
    category: "Flagship Municipal",
    description: "Re-engineered and modernized the entire municipal IT ecosystem for the City of Houston, MO. Built state-of-the-art, high-performance infrastructure for City Hall, Parks & Recreation, Electric Department, Public Works Department, Golf Course, Police Department, Fire Department, Storm Shelter, and the Welcome Center using clean CAT6 structured cabling.",
    stats: [
      { value: "Full Core", label: "System Modernized" },
      { value: "Multi-Dept", label: "Overhaul Scope" }
    ],
    links: [
      { text: "Engineered physical network laydowns & CAT6 structured cabling for City Hall, Parks & Recreation, Electric Department, Public Works, Golf Course, Police Department, Fire Department, Storm Shelter, and the Welcome Center", url: "#" },
      { text: "Deployed Lenovo ThinkCentre enterprise computers and secure department-wide workstations", url: "#" },
      { text: "Installed Rhombus physical security cameras with cloud analytics for municipal facility safety", url: "#" },
      { text: "Automated city council meeting recordings and established high-availability public broadcasts", url: "#" },
      { text: "Upfitted Police Department with Panasonic Toughbooks, vehicle bodycams, and ADF digital forensics software", url: "#" },
      { text: "Equipped patrol cars with Brother PocketJet thermal printers and fire trucks with Microsoft Surface tablets", url: "#" },
      { text: "Engineered high-performance network backbones using Ubiquiti infrastructure and Meraki enterprise firewalls", url: "#" }
    ],
    isFlagship: true
  },
  {
    name: "Cabool R-IV School District $1M+ Core Technology Overhaul",
    category: "Flagship Education",
    description: "Designed, engineered, and executed a complete technology overhaul exceeding $1,000,000 over a 5-year tenure for the Cabool R-IV School District. Modernized school systems and digital infrastructures into resilient, state-of-the-art education environments.",
    stats: [
      { value: "$1M+", label: "Capital Deployed" },
      { value: "5 Years", label: "Project Duration" }
    ],
    links: [
      { text: "Upgraded school core backbone bandwidth from a 10 Mbps line to 1 Gbps speeds" },
      { text: "Deployed HP Aruba core switches, advanced access points, and district-wide NAS systems" },
      { text: "Implemented secure DNS filtering and high-availability firewalls with full SSL Decryption" },
      { text: "Launched 1:1 student initiatives with new computer labs, Chromebook carts, and Promethean Boards" },
      { text: "Configured Active Directory Windows servers, Veeam infrastructure backups, and NAS storage integrations" },
      { text: "Integrated Clever SSO single sign-on, LanSchool classroom monitoring, and new Grandstream VoIP systems" },
      { text: "Full security district revamp including intercom repairs and Panasonic cameras with smart video insights" }
    ],
    isFlagship: true
  }
];

const WEBSITE_PROJECTS = [
  { 
    name: "City of Houston, Houston, MO", 
    url: "https://houstonmo.org", 
    category: "City & Community",
    description: "The official city website for local residents and visitors to easily pay utility bills, check community notices, and find municipal services online."
  },
  { 
    name: "Cabool School District, Cabool, MO", 
    url: "https://www.cabool.k12.mo.us", 
    category: "School District",
    description: "A central school hub where parents, students, and teachers can quickly check school news, upcoming events, teacher directories, and classroom resources."
  },
  { 
    name: "Cabool Alumni Association, Cabool, MO", 
    url: "https://alumni.cabool.k12.mo.us", 
    category: "Alumni Community",
    description: "A welcoming community website that brings alumni together, shares reunion updates, and helps support local student scholarships."
  },
  { 
    name: "Oak Hill R-1 School District, Salem, MO", 
    url: "https://district.oakhillr1.k12.mo.us", 
    category: "School District",
    description: "An easy-to-navigate school website that keeps families informed with weather alerts, lunch menus, event calendars, and staff contact information."
  },
  { 
    name: "High Country Timber & Stone, Licking, MO", 
    url: "https://highcountrytimberandstone.com", 
    category: "Local Business",
    description: "A clean, polished business website that showcases natural stone and timber products, making it simple for customers to browse photos and request a quote."
  },
  { 
    name: "Crocker Consulting, Springfield, MO", 
    url: "https://crockerengineers.com/", 
    category: "Business Website", 
    availableSoon: true,
    description: "A professional company website presenting structural engineering services and past projects. Currently getting finishing touches before its public launch."
  },
  { 
    name: "New Life Free Will Baptist Church, Licking, MO", 
    category: "Church & Ministry",
    description: "A warm church website that shares Sunday service times, upcoming fellowship gatherings, sermons, and community outreach programs."
  },
  { 
    name: "Abounding Hope Church, Licking, MO", 
    category: "Church & Ministry",
    description: "An inviting online home for church members and visitors to find worship times, special events, prayer requests, and community activities."
  }
];

const PORTFOLIO_PROJECTS = [...SHOWCASE_PROJECTS, ...WEBSITE_PROJECTS];

const CERTIFICATIONS = [
  {
    name: "CompTIA A+ & Network+",
    desc: "Validates core skills in hardware diagnostics, software troubleshooting, operating systems, security protocols, and enterprise routing/switching infrastructure."
  },
  {
    name: "Light Brigade Certified Fiber Optics Professional",
    desc: "Rigorous industry certification covering fiber optic network design, installation safety standards, high-precision fusion splicing, and optical loss testing."
  },
  {
    name: "Microsoft Administer Active Directory",
    desc: "Expert-level competency in domain controller configuration, user group permissions, secure object management, and Active Directory server architecture."
  },
  {
    name: "Google Technical Support Fundamentals",
    desc: "Authorized Google certification covering systems troubleshooting, customer service frameworks, hardware configurations, and core IT infrastructure."
  },
  {
    name: "Artificial Intelligence Proficiency",
    desc: "Specialized certification demonstrating hands-on proficiency in engineering prompts, contextual fine-tuning, and integrating advanced neural network models."
  },
  {
    name: "Hubspot Digital Marketing & SEO",
    desc: "Comprehensive validation in search engine optimization (SEO), digital marketing conversion funnels, content strategy, and organic visibility."
  },
  {
    name: "Canva Essentials",
    desc: "Official credential verifying visual communication skills, digital asset creation, brand identity management, layout design principles, and collaborative graphic production."
  },
  {
    name: "Skills USA Computer Maintenance Proficiency",
    desc: "National competitive standard validating real-world hardware diagnostics, system imaging, OS deployment, and hands-on technical support."
  },
  {
    name: "Technology Incubation Certified Server Admin",
    desc: "Advanced credential verifying server host management, system backup and disaster recovery, Hyper-V/VMware clustering, and storage provisioning."
  },
  {
    name: "Technology Incubation Certified Hardware Associate",
    desc: "Validates technical proficiency in motherboard component diagnostic workflows, physical component testing, and specialized IT hardware configurations."
  },
  {
    name: "TestOut Network Professional",
    desc: "Simulated-lab mastery validating enterprise router and switch setups, VLAN configurations, subnet management, IP allocation, and firewall administration."
  },
  {
    name: "CJIS Security & Privacy",
    desc: "Federal and State regulated FBI law enforcement-grade credential verifying security protocols for managing and storing sensitive criminal justice data."
  },
  {
    name: "OSHA Bucket Truck Operations/Safety",
    desc: "Safety compliance certification covering high-voltage line clearance, aerial lift safety standards, and rigorous occupational workspace hazard prevention."
  }
];

const TABS = [
  { id: "home", label: "Home", icon: <Home className="w-4 h-4" /> },
  { id: "about", label: "About Randon", icon: <User className="w-4 h-4" /> },
  { id: "resume", label: "Experience", icon: <Briefcase className="w-4 h-4" /> },
  { id: "services", label: "Services", icon: <Cpu className="w-4 h-4" /> },
  { id: "prices", label: "Prices", icon: <DollarSign className="w-4 h-4" /> },
  { id: "estimator", label: "Price Estimator", icon: <Calculator className="w-4 h-4" /> },
  { id: "expertise", label: "Expertise", icon: <Award className="w-4 h-4" /> },
  { id: "portfolio", label: "Project Showcase", icon: <Briefcase className="w-4 h-4" /> },
  { id: "websites", label: "Websites", icon: <Globe className="w-4 h-4" /> },
  { id: "books", label: "Books", icon: <BookOpen className="w-4 h-4" /> },
  { id: "portal", label: "Client Portal", icon: <CreditCard className="w-4 h-4" /> },
  { id: "apps", label: "AI Apps", icon: <Zap className="w-4 h-4" /> },
  { id: "contact", label: "Contact", icon: <Mail className="w-4 h-4" /> }
];

export default function App() {
  const cookieConsent = useCookieConsent();
  const [portraitFailed, setPortraitFailed] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("home");
  const [revealedEmail, setRevealedEmail] = useState(false);
  const [revealedPhone, setRevealedPhone] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredWordIndex, setHoveredWordIndex] = useState<number | null>(null);
  const mainRef = React.useRef<HTMLElement>(null);

  const scrollToTop = () => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    setShowScrollTop(false);
  }, [activeTab]);

  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = 'mailto:help@randonbrown.com';
  };

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = 'tel:3149443074';
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Ensure document.title matches requested website and SEO title
  useEffect(() => {
    document.title = "Randon Brown, LLC | Texas County, MO | Computer Repair | Websites & More";
  }, []);

  // Global keyboard shortcut to trigger spotlight search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      // Do not trigger search if user is actively writing command in the terminal or filling forms
      if (isInput) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      } else if (e.key === '/') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);
  
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isCookiePolicyOpen, setIsCookiePolicyOpen] = useState(false);
  const [currentAdminUser, setCurrentAdminUser] = useState<FirebaseUser | null>(null);
  const [isNavHubOpen, setIsNavHubOpen] = useState(false);
  const [quotePrefill, setQuotePrefill] = useState<string>('');
  const [quoteModePrefill, setQuoteModePrefill] = useState<'onsite' | 'pickup' | 'remote' | undefined>(undefined);

  // Monitor Firebase auth state for admin indicator in footer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentAdminUser(user);
    });
    return () => unsubscribe();
  }, []);
  const [selectedLetter, setSelectedLetter] = useState<typeof RECOMMENDATIONS[0] | null>(null);
  const [selectedSkillIndex, setSelectedSkillIndex] = useState(0);
  const [selectedRecIndex, setSelectedRecIndex] = useState(0);
  const [isTestimonialHovered, setIsTestimonialHovered] = useState(false);
  const [selectedWebIndex, setSelectedWebIndex] = useState(0);
  const [expandedCerts, setExpandedCerts] = useState<Record<number, boolean>>({});
  const [activeSkillItem, setActiveSkillItem] = useState<string | null>(null);

  // Sync hash with active tab
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash === "testimonials") {
        setActiveTab("home");
        setTimeout(() => {
          const el = document.getElementById("home-testimonials");
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      } else if (hash && TABS.some(t => t.id === hash)) {
        setActiveTab(hash);
      }
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const goToTab = (tabId: string) => {
    if (tabId === "testimonials") {
      setActiveTab("home");
      window.location.hash = "testimonials";
      setIsMenuOpen(false);
      setTimeout(() => {
        const el = document.getElementById("home-testimonials");
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    setActiveTab(tabId);
    window.location.hash = tabId;
    setIsMenuOpen(false);
  };

  const [darkMode, setDarkMode] = useState(true);

  // Simple animated subtitle typing for that professional look
  const [typingText, setTypingText] = useState("IT Leader & Technology Professional");
  useEffect(() => {
    const roles = [
      "IT Leader & Technology Professional",
      "Published Children's Author",
      "Website Architect & Logo Designer",
      "Musical Collaborator"
    ];
    let currentIdx = 0;
    const interval = setInterval(() => {
      currentIdx = (currentIdx + 1) % roles.length;
      setTypingText(roles[currentIdx]);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  // Testimonials Auto-cycling (clean & professional fade, pauses on hover)
  useEffect(() => {
    if (activeTab !== "home" || isTestimonialHovered) return;
    const interval = setInterval(() => {
      setSelectedRecIndex((prev) => (prev + 1) % RECOMMENDATIONS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, isTestimonialHovered]);

  return (
    <>
      <div className="min-h-screen w-full bg-[#060606] text-slate-100 font-sans selection:bg-brand-500/20 selection:text-brand-300 flex flex-col relative overflow-x-hidden">
        {/* Top Cookie Banner spanning the whole page */}
        <AnimatePresence>
          {cookieConsent.showBanner && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="w-full bg-[#0c0c0c]/98 border-b border-neutral-900 text-slate-200 z-[100] shrink-0 relative overflow-hidden backdrop-blur-md shadow-2xl"
            >
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2.5 text-center md:text-left flex-wrap md:flex-nowrap justify-center md:justify-start">
                  <Cookie className="w-4 h-4 text-brand-400 shrink-0 animate-gold-pulse" />
                  <span className="text-slate-300 font-sans">
                    We use cookies and local storage to remember your preferences and deliver high-performance tools. Zero advertising trackers.
                  </span>
                  <button
                    onClick={() => setIsCookiePolicyOpen(true)}
                    className="text-brand-400 hover:text-brand-300 underline font-semibold cursor-pointer shrink-0 ml-1"
                  >
                    Cookie Policy
                  </button>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={cookieConsent.acceptEssential}
                    className="px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer active:scale-95"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={cookieConsent.acceptAll}
                    className="px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-brand-500 to-amber-500 hover:from-brand-400 hover:to-amber-400 text-neutral-950 text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-md shadow-brand-500/20 active:scale-95"
                  >
                    <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Accept All</span>
                  </button>
                  <button
                    onClick={cookieConsent.acceptEssential}
                    className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-neutral-900 rounded-lg transition-colors cursor-pointer"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 w-full relative p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-0">
          {/* Pulsing Orange Border Laser Lights Moving Around Border */}
          <div className="absolute inset-2 md:inset-4 lg:inset-6 pointer-events-none z-50">
            <svg className="w-full h-full overflow-visible" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="laserGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="transparent" stopOpacity="0" />
                  <stop offset="25%" stopColor="#f59e0b" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#fbbf24" stopOpacity="1" />
                  <stop offset="70%" stopColor="#d97706" stopOpacity="1" />
                  <stop offset="85%" stopColor="#f59e0b" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </linearGradient>
                <filter id="neonGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              
              {/* Subtle base track */}
              <rect 
                x="1.5" 
                y="1.5" 
                width="calc(100% - 3px)" 
                height="calc(100% - 3px)" 
                rx="24" 
                ry="24" 
                fill="none" 
                stroke="#1c1917" 
                strokeWidth="1.5" 
              />
              
              {/* Moving glowing laser light beam */}
              <rect 
                x="1.5" 
                y="1.5" 
                width="calc(100% - 3px)" 
                height="calc(100% - 3px)" 
                rx="24" 
                ry="24" 
                fill="none" 
                stroke="url(#laserGradient)" 
                strokeWidth="3.5" 
                filter="url(#neonGlow)"
                className="animate-laser-beam"
              />
            </svg>
          </div>

          {/* Main Container */}
          <div className={`w-full ${cookieConsent.showBanner ? 'h-[calc(100vh-5.5rem)] md:h-[calc(100vh-6.5rem)] lg:h-[calc(100vh-7.5rem)]' : 'h-[calc(100vh-2rem)] md:h-[calc(100vh-3rem)] lg:h-[calc(100vh-4rem)]'} bg-[#0c0c0c] rounded-3xl border border-brand-500/35 shadow-[0_0_50px_rgba(245,158,11,0.15)] overflow-hidden flex flex-col md:flex-row relative z-10 transition-all duration-300`}>
        
        {/* Absolute Grayscale Background Portrait on Home screen */}
        <AnimatePresence>
          {activeTab === "home" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              className="absolute inset-0 pointer-events-none overflow-hidden z-0"
            >
              {!portraitFailed && (
                <picture className="absolute right-0 bottom-0 top-0 w-full md:w-[55%] h-full">
                  <img 
                    src={portraitImage} 
                    alt="Randon Brown - IT Leader and Systems Specialist" 
                    loading="eager"
                    decoding="async"
                    onError={() => setPortraitFailed(true)}
                    className="w-full h-full object-cover object-center brightness-105 contrast-105 select-none"
                  />
                </picture>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0c0c0c] via-[#0c0c0c]/80 to-[#0c0c0c]/15" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent opacity-80" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ----------------- SIDEBAR (Desktop) ----------------- */}
        <aside className="hidden md:flex flex-col w-64 lg:w-72 border-r border-neutral-900 bg-[#090909] p-4 lg:p-5 shrink-0 relative z-20 overflow-y-auto custom-scrollbar overflow-x-hidden">
          <div className="flex flex-col justify-between min-h-full">
            <div>
              {/* Header / Logo */}
              <div 
                onClick={() => goToTab("home")}
                className="cursor-pointer select-none mb-4 lg:mb-5 flex items-center gap-3 group relative"
              >
                <BrandLogo size="sm" className="shrink-0 transition-transform duration-500 group-hover:rotate-12" />
                <div className="flex items-center min-w-0">
                  <span className="text-base lg:text-lg font-display font-black tracking-tight text-white group-hover:text-slate-200 transition-all duration-300 whitespace-nowrap">
                    Randon Brown, <span className="text-brand-400 font-extrabold">LLC</span>
                  </span>
                </div>
              </div>

              {/* Search Site Trigger */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="w-full mb-3.5 lg:mb-4 px-3 py-2 bg-[#0c0c0c] hover:bg-neutral-900 border border-neutral-900 hover:border-brand-500/20 rounded-xl flex items-center justify-between text-slate-500 hover:text-slate-300 transition-all text-xs font-medium group cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Search className="w-3.5 h-3.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                  <span>Select to Search</span>
                </div>
                <span className="text-[10px] font-mono text-slate-600 group-hover:text-slate-400 border border-neutral-900 bg-neutral-950 px-1.5 py-0.5 rounded transition-colors select-none">
                  ⌘K
                </span>
              </button>

              {/* Vertical Navigation Menu */}
              <nav className="flex flex-col gap-0.5 lg:gap-1">
                {TABS.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => goToTab(tab.id)}
                      className={`flex items-center gap-3 px-3 py-2 lg:py-2.5 rounded-xl font-medium text-xs lg:text-sm transition-all duration-300 text-left cursor-pointer group select-none relative overflow-hidden ${
                        isActive 
                          ? "text-brand-500 animate-gold-pulse font-semibold bg-brand-500/[0.04]" 
                          : "text-slate-400 hover:text-white hover:bg-white/[0.02]"
                      }`}
                    >
                      <motion.span 
                        className={`inline-block transition-colors duration-300 ${isActive ? "text-brand-500" : "text-slate-500 group-hover:text-slate-300"}`}
                        animate={isActive ? {
                          y: [0, -1.5, 0, 1.5, 0],
                          rotate: [0, 4, 0, -4, 0],
                          scale: [1, 1.08, 1, 1.08, 1],
                        } : {}}
                        whileHover={{
                          scale: 1.2,
                          rotate: 12,
                          y: -2,
                        }}
                        transition={isActive ? {
                          y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                          rotate: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                          scale: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                        } : {
                          type: "spring",
                          stiffness: 200,
                          damping: 10
                        }}
                      >
                        {tab.icon}
                      </motion.span>
                      <span className="truncate">{tab.label}</span>

                      {isActive && (
                        <motion.div 
                          layoutId="activeIndicator"
                          className="absolute left-0 top-1/4 bottom-1/4 w-[3px] bg-brand-500 rounded-r-md animate-gold-pulse"
                        />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Sidebar Weather Widget */}
            <div className="pt-2 my-1 border-t border-neutral-900/80 flex justify-center w-full">
              <WeatherWidget />
            </div>

            {/* Sidebar Footer Utilities */}
            <div className="pt-3 mt-1 border-t border-neutral-900">
              <div className="flex items-center justify-between gap-1">
                <button 
                  onClick={() => setIsPrivacyOpen(true)}
                  className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                  title="Privacy Policy"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                </button>

                <a 
                  href="https://www.linkedin.com/in/randon-brown-7139ab127" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                  title="LinkedIn Profile"
                  aria-label="Visit Randon Brown on LinkedIn"
                >
                  <LinkedInIcon className="w-3.5 h-3.5" />
                </a>

                <a 
                  href="https://www.youtube.com/@RandonBrownLLC" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                  title="YouTube Channel"
                  aria-label="Visit Randon Brown on YouTube"
                >
                  <YouTubeIcon className="w-3.5 h-3.5" />
                </a>

                <button 
                  onClick={handlePhoneClick}
                  className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                  title="Call Phone (Secure)"
                >
                  <Phone className="w-3.5 h-3.5" />
                </button>

                <button 
                  onClick={handleEmailClick}
                  className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                  title="Send Email (Secure)"
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* ----------------- MOBILE TOP BAR ----------------- */}
        <header className="md:hidden flex items-center justify-between px-5 py-3.5 border-b border-neutral-900 bg-[#090909] sticky top-0 z-40">
          <div 
            onClick={() => goToTab("home")}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <BrandLogo size="sm" className="w-8 h-8 shrink-0" />
            <span className="text-base font-display font-black tracking-tight text-white whitespace-nowrap">
              Randon Brown, <span className="text-brand-400 font-extrabold">LLC</span>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 text-slate-400 hover:text-brand-500 cursor-pointer"
              title="Search Site"
            >
              <Search className="w-4.5 h-4.5" />
            </button>
            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-2 text-slate-400 hover:text-brand-500 cursor-pointer"
              aria-label="Open Navigation Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Mobile Slide-Out Menu Drawer */}
        <AnimatePresence>
          {isMenuOpen && (
            <div className="fixed inset-0 z-50 md:hidden flex justify-end">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setIsMenuOpen(false)}
              />

              <motion.div 
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 220 }}
                className="relative w-72 sm:w-80 max-w-[85vw] h-full bg-[#0c0c0c] border-l border-neutral-900 p-5 sm:p-6 flex flex-col justify-between overflow-y-auto custom-scrollbar z-10"
              >
                <div>
                  <div className="flex items-center justify-between mb-6 pb-3 border-b border-neutral-900">
                    <div className="flex items-center gap-2.5">
                      <BrandLogo size="sm" className="w-7 h-7" />
                      <span className="text-xs font-mono text-slate-400 uppercase tracking-widest">Navigation</span>
                    </div>
                    <button 
                      onClick={() => setIsMenuOpen(false)}
                      className="p-2 hover:bg-white/[0.02] rounded-full text-slate-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Mobile Search Option */}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsSearchOpen(true);
                    }}
                    className="w-full mb-5 px-3.5 py-2.5 bg-brand-500/10 hover:bg-brand-500/15 border border-brand-500/20 text-brand-400 rounded-xl flex items-center gap-3 text-xs font-bold transition-all cursor-pointer"
                  >
                    <Search className="w-4 h-4 text-brand-400" />
                    <span>Select to Search</span>
                  </button>

                  <nav className="flex flex-col gap-1">
                    {TABS.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => goToTab(tab.id)}
                          className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-medium text-sm transition-all text-left ${
                            isActive 
                              ? "text-brand-500 bg-brand-500/[0.03] border border-brand-500/10" 
                              : "text-slate-400 hover:text-white"
                          }`}
                        >
                          <span className={isActive ? "text-brand-500" : "text-slate-500"}>{tab.icon}</span>
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Mobile Drawer Weather Widget */}
                <div className="py-2 flex justify-center w-full border-t border-neutral-900/60">
                  <WeatherWidget />
                </div>

                 <div className="space-y-4 pt-4 border-t border-neutral-900">
                  <div className="flex items-center justify-around">
                    <button 
                      onClick={() => {
                        setIsPrivacyOpen(true);
                        setIsMenuOpen(false);
                      }}
                      className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                      title="Privacy Policy"
                    >
                      <ShieldCheck className="w-5 h-5" />
                    </button>

                    <a 
                      href="https://www.linkedin.com/in/randon-brown-7139ab127" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                      title="LinkedIn Profile"
                      aria-label="Visit Randon Brown on LinkedIn"
                    >
                      <LinkedInIcon className="w-5 h-5" />
                    </a>

                    <a 
                      href="https://www.youtube.com/@RandonBrownLLC" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                      title="YouTube Channel"
                      aria-label="Visit Randon Brown on YouTube"
                    >
                      <YouTubeIcon className="w-5 h-5" />
                    </a>

                    <button 
                      onClick={handlePhoneClick}
                      className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                      title="Call Phone (Secure)"
                    >
                      <Phone className="w-5 h-5" />
                    </button>

                    <button 
                      onClick={handleEmailClick}
                      className="p-1.5 text-slate-500 hover:text-brand-500 hover:bg-white/[0.03] hover:animate-gold-pulse transition-all rounded-lg cursor-pointer"
                      title="Send Email (Secure)"
                    >
                      <Mail className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* ----------------- MAIN CONTENT CONTAINER ----------------- */}
        <main 
          ref={mainRef}
          onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 500)}
          className="flex-1 min-w-0 overflow-y-auto custom-scrollbar relative z-10 flex flex-col justify-between"
        >
          <div className="p-3.5 sm:p-6 md:p-10 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 15, scale: 0.98, filter: "blur(6px) brightness(0.9)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px) brightness(1)" }}
                exit={{ 
                  opacity: 0, 
                  y: -45, 
                  scale: 1.04, 
                  filter: "blur(18px) brightness(1.6)",
                  transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] }
                }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                
                {/* ----------------- HOME TAB ----------------- */}
                {activeTab === "home" && (
                  <div className="min-h-[70vh] grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start relative z-10 py-4 sm:py-8">
                    
                    {/* LEFT COLUMN: All text in the left column and centered within the left column */}
                    <div className="w-full max-w-xl mx-auto flex flex-col items-center justify-center text-center space-y-6">
                      
                      <div className="space-y-2 text-center">
                        <p className="text-slate-400 font-medium tracking-wide text-base">Meet IT Leader & Technologist</p>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-white tracking-tight leading-none">
                          Randon Brown
                        </h1>
                      </div>

                      <div className="min-h-[2.5rem] flex items-center justify-center overflow-hidden text-center">
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={typingText}
                            initial={{ y: 15, opacity: 0, filter: "blur(4px)" }}
                            animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                            exit={{ y: -15, opacity: 0, filter: "blur(4px)" }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="text-lg sm:text-xl md:text-2xl text-brand-400 font-bold font-display tracking-wide animate-gold-text-pulse text-center"
                          >
                            {typingText}
                          </motion.p>
                        </AnimatePresence>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="my-4 sm:my-6 w-full max-w-lg mx-auto flex flex-col items-center justify-center select-none cursor-pointer group text-center px-2"
                        onMouseEnter={() => setHoveredWordIndex(1)}
                        onMouseLeave={() => setHoveredWordIndex(null)}
                      >
                        {/* Elegant Typography Display */}
                        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight leading-relaxed text-center text-neutral-200 transition-colors duration-300">
                          Building{" "}
                          <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-amber-400 font-extrabold transition-all duration-300 group-hover:text-shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                            connections
                          </span>{" "}
                          that{" "}
                          <span className="text-white font-extrabold transition-all duration-300 group-hover:text-brand-300 group-hover:scale-105 inline-block">
                            inspire
                          </span>
                        </h2>

                        {/* Interactive Wave Connector System */}
                        <div className="relative w-full h-8 mt-4 overflow-visible flex items-center justify-center">
                          {/* 1. SEPARATED WAVES (Visible on Idle) */}
                          <AnimatePresence>
                            {hoveredWordIndex !== 1 && (
                              <motion.div
                                className="absolute inset-0 w-full h-full"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.7 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                              >
                                <svg 
                                  className="w-full h-full overflow-visible" 
                                  viewBox="0 0 400 40" 
                                  preserveAspectRatio="none"
                                >
                                  {/* Wave A - Golden Sine */}
                                  <motion.path
                                    d="M 0 20 Q 50 5, 100 20 T 200 20 T 300 20 T 400 20"
                                    fill="none"
                                    stroke="url(#wave-gradient-gold)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    animate={{
                                      d: [
                                        "M 0 20 Q 50 5, 100 20 T 200 20 T 300 20 T 400 20",
                                        "M 0 20 Q 50 35, 100 20 T 200 20 T 300 20 T 400 20",
                                        "M 0 20 Q 50 5, 100 20 T 200 20 T 300 20 T 400 20"
                                      ]
                                    }}
                                    transition={{
                                      duration: 3.5,
                                      repeat: Infinity,
                                      ease: "easeInOut"
                                    }}
                                  />

                                  {/* Wave B - Amber Cosine (Phase shifted) */}
                                  <motion.path
                                    d="M 0 20 Q 50 35, 100 20 T 200 20 T 300 20 T 400 20"
                                    fill="none"
                                    stroke="url(#wave-gradient-amber)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    animate={{
                                      d: [
                                        "M 0 20 Q 50 35, 100 20 T 200 20 T 300 20 T 400 20",
                                        "M 0 20 Q 50 5, 100 20 T 200 20 T 300 20 T 400 20",
                                        "M 0 20 Q 50 35, 100 20 T 200 20 T 300 20 T 400 20"
                                      ]
                                    }}
                                    transition={{
                                      duration: 3.5,
                                      repeat: Infinity,
                                      ease: "easeInOut",
                                      delay: 0.3
                                    }}
                                  />

                                  <defs>
                                    <linearGradient id="wave-gradient-gold" x1="0%" y1="0%" x2="100%" y2="0%">
                                      <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.2" />
                                      <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.8" />
                                      <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.2" />
                                    </linearGradient>
                                    <linearGradient id="wave-gradient-amber" x1="0%" y1="0%" x2="100%" y2="0%">
                                      <stop offset="0%" stopColor="#d97706" stopOpacity="0.2" />
                                      <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.8" />
                                      <stop offset="100%" stopColor="#d97706" stopOpacity="0.2" />
                                    </linearGradient>
                                  </defs>
                                </svg>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* 2. UNIFIED CONNECTOR BEAM (Visible on Hover) */}
                          <AnimatePresence>
                            {hoveredWordIndex === 1 && (
                              <motion.div
                                className="absolute inset-0 w-full h-full flex items-center justify-center pointer-events-none"
                                initial={{ opacity: 0, scaleX: 0.4 }}
                                animate={{ opacity: 1, scaleX: 1 }}
                                exit={{ opacity: 0, scaleX: 0.4 }}
                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                              >
                                {/* Outer Glow Aura */}
                                <div className="absolute w-[95%] h-[6px] bg-brand-500 rounded-full blur-[8px] opacity-80" />
                                
                                {/* Inner Laser beam */}
                                <div className="absolute w-[95%] h-[2px] bg-gradient-to-r from-transparent via-white to-transparent shadow-[0_0_12px_#fbbf24,0_0_24px_#f59e0b] rounded-full" />
                                
                                {/* Orbiting data packet traveling along the straight beam */}
                                <motion.div 
                                  className="absolute w-3 h-3 bg-white rounded-full shadow-[0_0_8px_#ffffff,0_0_15px_#f59e0b]"
                                  animate={{
                                    left: ["2.5%", "97.5%"]
                                  }}
                                  transition={{
                                    duration: 1.2,
                                    repeat: Infinity,
                                    ease: "linear"
                                  }}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>

                      {/* Primary Action Buttons: "I Need Something Fixed" & "I Need a Website" */}
                      <div className="pt-2 w-full flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                        <button
                          onClick={() => {
                            setActiveTab("service-quote");
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-6 py-3.5 bg-neutral-900/90 hover:bg-neutral-800 text-white hover:text-brand-300 rounded-2xl text-sm font-extrabold border border-brand-500/35 hover:border-brand-500/60 shadow-lg flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] cursor-pointer group"
                        >
                          <Wrench className="w-4 h-4 text-brand-400 group-hover:rotate-12 transition-transform shrink-0" />
                          <span>I Need Something Fixed</span>
                        </button>

                        <button
                          onClick={() => {
                            setActiveTab("website-onboarding");
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-6 py-3.5 bg-neutral-900/90 hover:bg-neutral-800 text-white hover:text-brand-300 rounded-2xl text-sm font-extrabold border border-brand-500/35 hover:border-brand-500/60 shadow-lg flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] cursor-pointer group"
                        >
                          <Sparkles className="w-4 h-4 text-brand-400 group-hover:scale-110 transition-transform shrink-0" />
                          <span>I Need a Website</span>
                        </button>
                      </div>

                      {/* Secondary Quick Links */}
                      <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
                        <button
                          onClick={() => setIsNavHubOpen(true)}
                          className="px-4 py-2 bg-neutral-900/60 hover:bg-neutral-800 text-slate-400 hover:text-slate-200 rounded-xl text-xs font-semibold border border-neutral-800/80 flex items-center gap-1.5 transition-all active:scale-[0.98] cursor-pointer"
                        >
                          Explore Profile <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => goToTab("contact")}
                          className="px-4 py-2 bg-neutral-900/60 hover:bg-neutral-800 text-slate-400 hover:text-slate-200 rounded-xl text-xs font-semibold border border-neutral-800/80 flex items-center gap-1.5 transition-all active:scale-[0.98] cursor-pointer"
                        >
                          Get In Touch
                        </button>
                      </div>

                      {/* ----------------- TESTIMONIALS SECTION ON HOME ----------------- */}
                      <div id="home-testimonials" className="w-full pt-8 pb-4 space-y-6 text-center border-t border-neutral-900/80">
                        {/* Centered Testimonial Card with Uniform Height */}
                        <div className="relative w-full min-h-[380px] sm:min-h-[400px]">
                          {/* Super cool theme-matching arrow protruding from the testimonial up toward Randon's chin */}
                          <div className={`hidden lg:block absolute -top-24 xl:-top-28 -right-8 lg:-right-20 xl:-right-32 w-72 lg:w-96 h-36 lg:h-40 pointer-events-none z-30 select-none transition-all duration-300 ${isTestimonialHovered ? "scale-105 brightness-125" : "opacity-95"}`}>
                            <svg 
                              className="w-full h-full overflow-visible" 
                              viewBox="0 0 340 140" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <defs>
                                <linearGradient id="homeArrowGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#d97706" stopOpacity="0.3" />
                                  <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.9" />
                                  <stop offset="100%" stopColor="#fbbf24" stopOpacity="1" />
                                </linearGradient>
                                <linearGradient id="homeTextGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#fef08a" />
                                  <stop offset="50%" stopColor="#fbbf24" />
                                  <stop offset="100%" stopColor="#f59e0b" />
                                </linearGradient>
                                <filter id="homeArrowGlow" x="-30%" y="-30%" width="160%" height="160%">
                                  <feGaussianBlur stdDeviation="3.5" result="blur" />
                                  <feMerge>
                                    <feMergeNode in="blur" />
                                    <feMergeNode in="SourceGraphic" />
                                  </feMerge>
                                </filter>
                                <filter id="textGlow" x="-20%" y="-20%" width="140%" height="140%">
                                  <feGaussianBlur stdDeviation="2" result="blur" />
                                  <feMerge>
                                    <feMergeNode in="blur" />
                                    <feMergeNode in="SourceGraphic" />
                                  </feMerge>
                                </filter>
                              </defs>

                              {/* Glowing Anchor Node on the Testimonial Card */}
                              <circle cx="20" cy="120" r="4.5" fill="#f59e0b" filter="url(#homeArrowGlow)" />
                              <circle cx="20" cy="120" r="9" stroke="#fbbf24" strokeWidth="1.5" opacity="0.6" className="animate-ping" />

                              {/* Outer Glow Halo Path */}
                              <path
                                d="M 20 120 C 60 85, 120 45, 205 20"
                                stroke="#f59e0b"
                                strokeWidth="5.5"
                                strokeLinecap="round"
                                opacity="0.3"
                                filter="url(#homeArrowGlow)"
                              />

                              {/* Core Laser Path */}
                              <path
                                d="M 20 120 C 60 85, 120 45, 205 20"
                                stroke="url(#homeArrowGrad)"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />

                              {/* Traveling Energy Pulse Packet */}
                              <motion.circle
                                r="3"
                                fill="#ffffff"
                                filter="url(#homeArrowGlow)"
                                animate={{
                                  cx: [20, 75, 140, 205],
                                  cy: [120, 78, 40, 20],
                                  opacity: [0, 1, 1, 0]
                                }}
                                transition={{
                                  duration: 2.2,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              />

                              {/* Sleek Arrowhead pointing up-right toward chin */}
                              <g transform="translate(205, 20) rotate(-18)">
                                <path
                                  d="M 0 0 L -15 -6 L -10 0 L -15 6 Z"
                                  fill="#fbbf24"
                                  stroke="#f59e0b"
                                  strokeWidth="1"
                                  filter="url(#homeArrowGlow)"
                                />
                              </g>

                              {/* Hand-drawn Font: "This Guy" after the arrow */}
                              <g transform="translate(216, 22) rotate(-8)">
                                <text
                                  x="0"
                                  y="0"
                                  className="font-handdrawn select-none"
                                  style={{ fontFamily: "'Permanent Marker', 'Caveat', cursive, sans-serif" }}
                                  fill="url(#homeTextGrad)"
                                  fontSize="26"
                                  letterSpacing="0.05em"
                                  filter="url(#textGlow)"
                                >
                                  This Guy
                                </text>
                                {/* Hand-drawn doodle underlines for authentic marker feel */}
                                <path
                                  d="M 2 8 C 30 11, 70 6, 114 9"
                                  stroke="#f59e0b"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  opacity="0.85"
                                  filter="url(#homeArrowGlow)"
                                />
                                <path
                                  d="M 12 13 C 35 15, 65 12, 102 14"
                                  stroke="#fbbf24"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  opacity="0.6"
                                />
                              </g>
                            </svg>
                          </div>

                          <AnimatePresence mode="wait">
                            <motion.div
                              key={selectedRecIndex}
                              drag="x"
                              dragConstraints={{ left: 0, right: 0 }}
                              dragElastic={0.2}
                              onDragEnd={(event, info) => {
                                const swipeThreshold = 50;
                                if (info.offset.x < -swipeThreshold) {
                                  setSelectedRecIndex(prev => (prev === RECOMMENDATIONS.length - 1 ? 0 : prev + 1));
                                } else if (info.offset.x > swipeThreshold) {
                                  setSelectedRecIndex(prev => (prev === 0 ? RECOMMENDATIONS.length - 1 : prev - 1));
                                }
                              }}
                              initial={{ opacity: 0, scale: 0.98, filter: "blur(6px)" }}
                              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                              exit={{ opacity: 0, scale: 1.02, filter: "blur(6px)" }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              onMouseEnter={() => setIsTestimonialHovered(true)}
                              onMouseLeave={() => setIsTestimonialHovered(false)}
                              className="flex flex-col justify-between p-6 sm:p-8 bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/50 shadow-[0_0_30px_rgba(245,158,11,0.05)] hover:shadow-[0_0_50px_rgba(245,158,11,0.15)] transition-all duration-300 rounded-3xl text-center relative select-none cursor-grab active:cursor-grabbing w-full min-h-[380px] sm:min-h-[400px] h-[380px] sm:h-[400px] overflow-hidden"
                            >
                              {/* Background Quotes */}
                              <motion.div 
                                className="absolute top-5 left-5 text-brand-500 pointer-events-none"
                                animate={{
                                  y: [0, -8, 0],
                                  scale: [1, 1.1, 1],
                                  opacity: [0.10, 0.32, 0.10],
                                  rotate: [0, -6, 0]
                                }}
                                transition={{
                                  duration: 6,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              >
                                <Quote className="w-12 h-12" />
                              </motion.div>

                              <motion.div 
                                className="absolute bottom-5 right-5 text-brand-500 pointer-events-none"
                                animate={{
                                  y: [0, 8, 0],
                                  scale: [1, 1.1, 1],
                                  opacity: [0.10, 0.32, 0.10],
                                  rotate: [180, 186, 180]
                                }}
                                transition={{
                                  duration: 6,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                  delay: 3
                                }}
                              >
                                <Quote className="w-12 h-12" />
                              </motion.div>

                              {/* Decorative Star Rating Indicator (Fixed Top Slot) */}
                              <div className="flex gap-1.5 text-brand-400 justify-center h-5 items-center shrink-0">
                                {[...Array(5)].map((_, idx) => (
                                  <Zap key={idx} className="w-4 h-4 fill-current animate-gold-pulse" />
                                ))}
                              </div>

                              {/* Large Excerpt Quote (Vertically Centered Slot) */}
                              <div className="flex-1 flex items-center justify-center px-2 sm:px-6 my-auto">
                                <p className="text-sm sm:text-base text-slate-300 italic leading-relaxed font-sans text-center max-w-md">
                                  "{RECOMMENDATIONS[selectedRecIndex].excerpt}"
                                </p>
                              </div>

                              {/* Bottom Container: Author Details & Action Button (Fixed Bottom Slot) */}
                              <div className="space-y-4 shrink-0 pt-2">
                                <div className="space-y-1 text-center">
                                  <h3 className="text-lg sm:text-xl font-display font-bold text-white tracking-tight">
                                    {RECOMMENDATIONS[selectedRecIndex].name}
                                  </h3>
                                  <p className="text-xs text-slate-400 font-medium tracking-wide">
                                    {RECOMMENDATIONS[selectedRecIndex].role} &bull; <span className="text-brand-400">{RECOMMENDATIONS[selectedRecIndex].org}</span>
                                  </p>
                                </div>

                                <div className="flex justify-center">
                                  <button
                                    onClick={() => setSelectedLetter(RECOMMENDATIONS[selectedRecIndex])}
                                    className="px-5 py-2 bg-[#0c0c0c] text-brand-400 border border-neutral-900 rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-neutral-900 hover:border-brand-500/25 cursor-pointer shadow-md active:scale-95 transition-all"
                                  >
                                    Read Full Letter <ExternalLink className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          </AnimatePresence>

                          {/* Navigation Stepper Indicators & Controls */}
                          <div className="flex items-center justify-between gap-4 mt-4 px-2">
                            <div className="flex items-center gap-1.5">
                              {RECOMMENDATIONS.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setSelectedRecIndex(idx);
                                  }}
                                  className={`h-2 rounded-full transition-all cursor-pointer ${
                                    selectedRecIndex === idx 
                                      ? "bg-brand-500 w-5" 
                                      : "bg-neutral-800 hover:bg-neutral-700 w-2"
                                  }`}
                                  title={`Go to endorsement ${idx + 1}`}
                                />
                              ))}
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedRecIndex(prev => (prev === 0 ? RECOMMENDATIONS.length - 1 : prev - 1))}
                                className="h-8 w-8 bg-neutral-900 border border-neutral-800 hover:border-brand-500/30 text-slate-400 hover:text-brand-500 rounded-full transition-all cursor-pointer select-none active:scale-95 flex items-center justify-center shadow-lg"
                                title="Previous Endorsement"
                              >
                                <ChevronLeft className="w-4 h-4 transition-colors duration-300" />
                              </button>

                              <button
                                onClick={() => setSelectedRecIndex(prev => (prev === RECOMMENDATIONS.length - 1 ? 0 : prev + 1))}
                                className="h-8 w-8 bg-neutral-900 border border-neutral-800 hover:border-brand-500/30 text-slate-400 hover:text-brand-500 rounded-full transition-all cursor-pointer select-none active:scale-95 flex items-center justify-center shadow-lg"
                                title="Next Endorsement"
                              >
                                <ChevronRight className="w-4 h-4 transition-colors duration-300" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* RIGHT COLUMN: Dedicated Visual space for background portrait */}
                    <div className="relative hidden lg:flex flex-col items-end justify-start h-full pointer-events-none" />

                  </div>
                )}

                {/* ----------------- ABOUT TAB ----------------- */}
                {activeTab === "about" && (
                  <div className="space-y-10 max-w-6xl mx-auto">
                    {/* Centered Header block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none border border-brand-500/20">
                        <User className="w-3.5 h-3.5" />
                        About Randon Brown
                      </div>
                      <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white leading-tight tracking-tight">
                        A Legacy of Integrity, Dedicated to Service
                      </h2>
                      <p className="text-sm sm:text-base text-slate-400">
                        Husband, Father, Resilient IT Leader & Published Children's Author.
                      </p>
                      <div className="flex items-center justify-center gap-2 pt-1 text-xs text-slate-400 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-slate-300">
                          <MapPin className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                          Licking, Missouri
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-slate-300">
                          <Briefcase className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                          11+ Years Systems Leadership
                        </span>
                      </div>
                    </div>

                    {/* Main Story & Portrait Section: Portrait positioned on the RIGHT */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                      
                      {/* Left Column: Narrative Bio & Foundations (lg:col-span-7 xl:col-span-8) */}
                      <div className="lg:col-span-7 xl:col-span-8 space-y-6">
                        
                        {/* Bento Card 1: Resilience */}
                        <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-neutral-900/80 shadow-xl relative group hover:border-brand-500/25 transition-all duration-300">
                          <div className="absolute top-0 right-0 w-36 h-36 bg-brand-500/[0.03] rounded-full blur-3xl pointer-events-none overflow-hidden" />
                          
                          {/* Super cool theme-matching arrow protruding across and up toward Randon's chin in the portrait */}
                          <div className="hidden lg:block absolute -top-8 -right-10 xl:-right-14 w-48 xl:w-60 h-28 pointer-events-none z-30 select-none">
                            <svg className="w-full h-full overflow-visible" viewBox="0 0 250 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <defs>
                                <linearGradient id="aboutArrowGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#d97706" stopOpacity="0.4" />
                                  <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.9" />
                                  <stop offset="100%" stopColor="#fbbf24" stopOpacity="1" />
                                </linearGradient>
                                <linearGradient id="aboutTextGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#fef08a" />
                                  <stop offset="50%" stopColor="#fbbf24" />
                                  <stop offset="100%" stopColor="#f59e0b" />
                                </linearGradient>
                                <filter id="aboutArrowGlow" x="-30%" y="-30%" width="160%" height="160%">
                                  <feGaussianBlur stdDeviation="3" result="blur" />
                                  <feMerge>
                                    <feMergeNode in="blur" />
                                    <feMergeNode in="SourceGraphic" />
                                  </feMerge>
                                </filter>
                              </defs>

                              {/* Glowing Anchor Node on the card border */}
                              <circle cx="15" cy="80" r="4" fill="#f59e0b" filter="url(#aboutArrowGlow)" />
                              <circle cx="15" cy="80" r="8" stroke="#fbbf24" strokeWidth="1.5" opacity="0.6" className="animate-ping" />

                              {/* Outer glow stroke */}
                              <path
                                d="M 15 80 C 45 55, 85 30, 140 12"
                                stroke="#f59e0b"
                                strokeWidth="5"
                                strokeLinecap="round"
                                opacity="0.3"
                                filter="url(#aboutArrowGlow)"
                              />

                              {/* Core laser stroke */}
                              <path
                                d="M 15 80 C 45 55, 85 30, 140 12"
                                stroke="url(#aboutArrowGrad)"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />

                              {/* Traveling energy photon */}
                              <motion.circle
                                r="2.8"
                                fill="#ffffff"
                                filter="url(#aboutArrowGlow)"
                                animate={{
                                  cx: [15, 50, 95, 140],
                                  cy: [80, 52, 28, 12],
                                  opacity: [0, 1, 1, 0]
                                }}
                                transition={{
                                  duration: 2.2,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              />

                              {/* Arrowhead pointing up-right toward chin */}
                              <g transform="translate(140, 12) rotate(-16)">
                                <path
                                  d="M 0 0 L -13 -5 L -9 0 L -13 5 Z"
                                  fill="#fbbf24"
                                  stroke="#f59e0b"
                                  strokeWidth="1"
                                  filter="url(#aboutArrowGlow)"
                                />
                              </g>

                              {/* Hand-drawn Font: "This Guy" after the arrow */}
                              <g transform="translate(150, 14) rotate(-6)">
                                <text
                                  x="0"
                                  y="0"
                                  className="font-handdrawn select-none"
                                  style={{ fontFamily: "'Permanent Marker', 'Caveat', cursive, sans-serif" }}
                                  fill="url(#aboutTextGrad)"
                                  fontSize="22"
                                  letterSpacing="0.05em"
                                  filter="url(#aboutArrowGlow)"
                                >
                                  This Guy
                                </text>
                                {/* Hand-drawn doodle underline */}
                                <path
                                  d="M 2 7 C 24 9, 60 5, 96 8"
                                  stroke="#f59e0b"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  opacity="0.8"
                                />
                              </g>
                            </svg>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start">
                            <div className="p-3 bg-brand-500/10 text-brand-400 border border-brand-500/20 rounded-2xl shrink-0">
                              <Heart className="w-6 h-6 text-brand-400" />
                            </div>
                            <div className="space-y-3 flex-1">
                              <div>
                                <span className="text-[11px] font-mono uppercase tracking-wider text-brand-400 font-semibold">Origin & Tenacity</span>
                                <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                                  A Legacy of Resilience
                                </h3>
                              </div>
                              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                                Born at just 24 weeks in 1995, Randon Brown's life began with an uphill battle. Weighing barely over a pound and facing significant medical challenges, he defied every expectation from the start. That same resilience defines his approach to technology today—refusing to accept <span className="text-white font-semibold">"impossible"</span> as an answer.
                              </p>
                              <div className="mt-4 p-4 rounded-2xl bg-[#0a0a0a] border-l-4 border-brand-500 border-neutral-900 text-sm text-slate-300 italic leading-relaxed relative">
                                <Quote className="w-5 h-5 text-brand-500/30 absolute top-3 right-3" />
                                "The doctors once predicted he would never survive, warning he would remain in a vegetative state for life. Decades later, Randon stands as living proof that faith, prayer, and determination can overwrite any prognosis—no matter how big, or small."
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bento Card 2: Professional Mastery */}
                        <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-neutral-900/80 shadow-xl relative overflow-hidden group hover:border-brand-500/25 transition-all duration-300">
                          <div className="absolute top-0 right-0 w-36 h-36 bg-brand-500/[0.03] rounded-full blur-3xl pointer-events-none" />
                          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start">
                            <div className="p-3 bg-brand-500/10 text-brand-400 border border-brand-500/20 rounded-2xl shrink-0">
                              <Network className="w-6 h-6 text-brand-400" />
                            </div>
                            <div className="space-y-3 flex-1">
                              <div>
                                <span className="text-[11px] font-mono uppercase tracking-wider text-brand-400 font-semibold">Philosophy & Execution</span>
                                <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                                  IT Leadership & Hands-on Engineering
                                </h3>
                              </div>
                              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                                With over a decade of industry experience, a beautiful wife, three adorable children, and a cat oddly similar to a dog, Randon leads a life rooted in honesty and integrity. He is a versatile IT Professional who isn't afraid to get his hands dirty—whether he's pulling Ethernet wire, programming websites, or configuring enterprise switches, access points, and servers.
                              </p>
                              
                              {/* Strengths Badges */}
                              <div className="pt-2 flex flex-wrap gap-2">
                                <span className="px-3 py-1 rounded-xl bg-neutral-900 border border-neutral-800 text-xs font-medium text-slate-300">
                                  Fiber-to-the-Home (FTTH)
                                </span>
                                <span className="px-3 py-1 rounded-xl bg-neutral-900 border border-neutral-800 text-xs font-medium text-slate-300">
                                  Enterprise Network Architecture
                                </span>
                                <span className="px-3 py-1 rounded-xl bg-neutral-900 border border-neutral-800 text-xs font-medium text-slate-300">
                                  Modern Web & UI Design
                                </span>
                                <span className="px-3 py-1 rounded-xl bg-neutral-900 border border-neutral-800 text-xs font-medium text-slate-300">
                                  Municipal Governance & CIPA
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Right Column: Portrait Frame (Moved to the Right) */}
                      <div className="lg:col-span-5 xl:col-span-4 space-y-4 lg:sticky lg:top-4">
                        <div className="relative group rounded-3xl p-3 bg-neutral-950 border border-neutral-900/80 hover:border-brand-500/30 overflow-hidden shadow-2xl transition-all duration-300">
                          <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/10 via-transparent to-brand-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                          {/* Image Box */}
                          <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-neutral-900 relative bg-neutral-950 shadow-inner">
                            {!portraitFailed && (
                              <>
                                <picture className="w-full h-full block">
                                  <img 
                                    src={portraitImage} 
                                    alt="Randon Brown - Technology Professional and IT Leader" 
                                    loading="eager"
                                    decoding="async"
                                    onError={() => setPortraitFailed(true)}
                                    className="w-full h-full object-cover object-center brightness-105 contrast-105 transition-all duration-700 ease-out"
                                  />
                                </picture>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 pointer-events-none" />
                              </>
                            )}
                            
                            {/* Animated White Outline Sweep (Google Photos Style) */}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none z-20" xmlns="http://www.w3.org/2000/svg">
                              <rect
                                x="1"
                                y="1"
                                width="calc(100% - 2px)"
                                height="calc(100% - 2px)"
                                rx="15"
                                fill="none"
                                stroke="url(#whiteOutlineSweepGrad)"
                                strokeWidth="3"
                                strokeLinecap="round"
                                className="animate-outline-sweep"
                              />
                              <defs>
                                <linearGradient id="whiteOutlineSweepGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                                  <stop offset="30%" stopColor="#ffffff" stopOpacity="0.6" />
                                  <stop offset="70%" stopColor="#ffffff" stopOpacity="0.15" />
                                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0.01" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </div>
                          
                          <div className="pt-4 pb-2 text-center space-y-1 relative z-10">
                            <h3 className="font-display font-bold text-xl text-white">Randon Brown</h3>
                            <p className="text-sm text-brand-400 font-medium">IT Leader & Technology Professional</p>
                            <p className="text-xs text-slate-400 flex items-center justify-center gap-1 pt-1">
                              <MapPin className="w-3.5 h-3.5 text-brand-500" />
                              <span>Licking, Missouri</span>
                            </p>
                          </div>
                        </div>

                        {/* Quick Interactive Actions */}
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => goToTab("resume")}
                            className="py-2.5 px-3 bg-[#090909] hover:bg-neutral-900 text-slate-300 hover:text-white border border-neutral-900 hover:border-brand-500/20 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all select-none cursor-pointer group"
                          >
                            <Briefcase className="w-3.5 h-3.5 text-brand-400" />
                            <span>Read Timeline</span>
                            <ChevronRight className="w-3 h-3 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
                          </button>
                          <button
                            onClick={() => goToTab("contact")}
                            className="py-2.5 px-3 bg-[#090909] hover:bg-neutral-900 text-slate-300 hover:text-white border border-neutral-900 hover:border-brand-500/20 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all select-none cursor-pointer group"
                          >
                            <Mail className="w-3.5 h-3.5 text-brand-400" />
                            <span>Let's Connect</span>
                            <ChevronRight className="w-3 h-3 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
                          </button>
                        </div>
                      </div>

                    </div>

                    {/* CAREER JOURNEY HIGHLIGHTS: Key Proven Metrics */}
                    <div className="p-6 md:p-8 bg-neutral-950 border border-neutral-900/80 rounded-3xl space-y-6 shadow-xl relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-brand-500/[0.01] rounded-full blur-3xl pointer-events-none" />
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="space-y-1">
                          <h3 className="text-lg sm:text-xl font-display font-bold text-white flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-brand-500 rounded-full" />
                            Career Journey Highlights
                          </h3>
                          <p className="text-sm text-slate-400">Key metrics from a decade of field execution, municipal broadband, and enterprise leadership.</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: "Years Leading", value: "11+", category: "Experience", icon: <Briefcase className="w-5 h-5 text-brand-400" /> },
                          { label: "Project Value", value: "$3M+", category: "Scale", icon: <Zap className="w-5 h-5 text-brand-400" /> },
                          { label: "Miles of Wire", value: "18+", category: "Infrastructure", icon: <Server className="w-5 h-5 text-brand-400" /> },
                          { label: "Uptime Managed", value: "99.9%", category: "Operations", icon: <RefreshCw className="w-5 h-5 text-brand-400" /> }
                        ].map((stat, idx) => (
                          <div key={idx} className="p-4 sm:p-5 rounded-2xl bg-[#0c0c0c] border border-neutral-900/80 hover:border-brand-500/20 hover:bg-[#090909] transition-all duration-300">
                            <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
                              <span className="text-xs font-mono text-slate-400 uppercase tracking-wider truncate min-w-0 mr-1">
                                {stat.category}
                              </span>
                              <div className="shrink-0 text-brand-400 flex items-center justify-center">
                                {stat.icon}
                              </div>
                            </div>
                            <div className="text-2xl sm:text-3xl font-bold text-white font-display tabular-nums">{stat.value}</div>
                            <div className="text-xs sm:text-sm text-slate-400 font-medium mt-1">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Elegant horizontal divider with subtle gradient */}
                    <div className="h-px bg-gradient-to-r from-transparent via-brand-500/15 to-transparent my-10" />

                    {/* Side-by-Side: What Sets Randon Apart & Creative Interests filling the space beside it */}
                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                      {/* Left Side: What Sets Randon Apart (lg:col-span-5) */}
                      <div className="lg:col-span-5">
                        <div className="p-6 sm:p-7 rounded-3xl bg-neutral-950 border border-neutral-900/80 shadow-xl space-y-5">
                          <div className="flex items-center justify-between pb-3 border-b border-neutral-900/80">
                            <div className="space-y-1">
                              <h3 className="text-lg sm:text-xl font-display font-bold text-white flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-brand-500 rounded-full" />
                                What Sets Randon Apart
                              </h3>
                              <p className="text-xs text-slate-400">Core principles forged across a decade in the field.</p>
                            </div>
                            <Sparkles className="w-5 h-5 text-brand-500 shrink-0" />
                          </div>
                          
                          <div className="space-y-3.5 text-left">
                            <div className="p-4 sm:p-4.5 rounded-2xl bg-[#0c0c0c] border border-neutral-900/80 hover:border-brand-500/20 transition-colors">
                              <h5 className="text-sm font-semibold text-white flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
                                Hands-On
                              </h5>
                              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mt-1.5">
                                He doesn't just write instructions. Randon actively pulls the cabling, configures switches, routers, servers, and stays hands-on!
                              </p>
                            </div>

                            <div className="p-4 sm:p-4.5 rounded-2xl bg-[#0c0c0c] border border-neutral-900/80 hover:border-brand-500/20 transition-colors">
                              <h5 className="text-sm font-semibold text-white flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
                                Deep-Rooted Integrity
                              </h5>
                              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mt-1.5">
                                Character and honest communication are his highest values. Every connection, contract, and deployment is executed with precision and complete transparency.
                              </p>
                            </div>

                            <div className="p-4 sm:p-4.5 rounded-2xl bg-[#0c0c0c] border border-neutral-900/80 hover:border-brand-500/20 transition-colors">
                              <h5 className="text-sm font-semibold text-white flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
                                Absolute Resilience
                              </h5>
                              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mt-1.5">
                                Overcoming extreme odds since birth has forged an unwavering problem-solving mindset. He tackles complex systems and difficult situations with professionalism.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Side: Creative Interests & Side Projects (lg:col-span-7, filling the space beside What Sets Randon Apart) */}
                      <div className="lg:col-span-7">
                        <div className="p-6 sm:p-7 bg-neutral-950 border border-neutral-900/80 rounded-3xl space-y-5 shadow-xl relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-48 h-48 bg-brand-500/[0.01] rounded-full blur-3xl pointer-events-none" />
                          <div className="space-y-1 pb-3 border-b border-neutral-900/80">
                            <h3 className="text-lg sm:text-xl font-display font-bold text-white flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-brand-500 rounded-full" />
                              Creative Interests & Side Projects
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-400">Ventures in creative composition, children's literature, culinary arts, and family life.</p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            {INTERESTS.map((interest, i) => {
                              const hasLink = 'link' in interest && interest.link;
                              const isMusic = interest.label === "Music Collaboration";
                              return (
                                <div key={i} className="h-full">
                                  {hasLink ? (
                                    <a 
                                      href={(interest as any).link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`${interest.label} - ${isMusic ? 'Listen on Apple Music' : 'View on Amazon'}`}
                                      className="p-4 sm:p-4.5 h-full rounded-2xl bg-[#0c0c0c] border border-neutral-900 hover:border-brand-500/30 hover:bg-[#090909] transition-all duration-300 flex flex-col justify-between group"
                                    >
                                      <div className="space-y-2.5">
                                        <div className="flex items-center justify-between">
                                          <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl group-hover:scale-105 transition-transform duration-300">
                                            {interest.icon}
                                          </div>
                                          <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-brand-400 transition-colors shrink-0" />
                                        </div>
                                        <div>
                                          <h4 className="font-bold text-white text-sm sm:text-base group-hover:text-brand-400 transition-colors">{interest.label}</h4>
                                          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mt-1">{interest.desc}</p>
                                        </div>
                                      </div>
                                      <div className="pt-3 mt-3 border-t border-neutral-900/60">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-400 group-hover:text-brand-300">
                                          {isMusic ? "Listen on Apple Music" : "View on Amazon"} <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                        </span>
                                      </div>
                                    </a>
                                  ) : (
                                    <div className="p-4 sm:p-4.5 h-full rounded-2xl bg-[#0c0c0c] border border-neutral-900 flex flex-col justify-between gap-3">
                                      <div className="space-y-2.5">
                                        <div className="p-2 bg-neutral-900 text-brand-400 rounded-xl w-fit">
                                          {interest.icon}
                                        </div>
                                        <div>
                                          <h4 className="font-bold text-white text-sm sm:text-base">{interest.label}</h4>
                                          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mt-1">{interest.desc}</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ----------------- RESUME TAB ----------------- */}
                {activeTab === "resume" && (
                  <div className="space-y-10 sm:space-y-12 max-w-6xl mx-auto px-2 sm:px-4">
                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        <Briefcase className="w-3.5 h-3.5" />
                        Career History & Credentials
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">
                        Experience & Qualifications
                      </h2>
                      <p className="text-sm text-slate-400">
                        Over a decade of advancing technology infrastructure, high-availability municipal broadband systems, and high-performance web engineering.
                      </p>
                    </div>

                    {/* Stats Metric Cards Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {STATS.slice(0, 8).map((stat, i) => (
                        <div
                          key={i}
                          className="p-5 rounded-2xl bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/40 shadow-lg flex flex-col justify-between transition-all group"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl group-hover:scale-105 transition-transform">
                              {stat.icon}
                            </div>
                            <span className="text-[9px] text-slate-500 font-mono font-bold uppercase tracking-widest">{stat.category}</span>
                          </div>
                          <div>
                            <div className="text-2xl font-display font-bold text-white tracking-tight">{stat.value}</div>
                            <div className="text-xs text-slate-400 font-semibold mt-0.5">{stat.label}</div>
                            {stat.subLabel && <div className="text-[10px] text-brand-400/80 font-mono mt-1">{stat.subLabel}</div>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Section 1: Professional Experience (Two-Column Grid) */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 border-b border-neutral-900 pb-4">
                        <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                          <Briefcase className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                            Professional Experience
                          </h3>
                          <p className="text-xs text-slate-400">Chronological history of leadership, systems administration, and engineering roles.</p>
                        </div>
                      </div>

                      {/* Two-Column Grid of Experience Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                        {EXPERIENCE.map((exp, expIdx) => {
                          const isFlagship = expIdx === 3; // City of Houston $3M+ Fiber
                          const tags = 
                            expIdx === 0 ? ["Web Design", "Consulting", "DNS & Hosting", "UI/UX", "Brand Identity"] :
                            expIdx === 1 ? ["Network Rebuild", "Active Directory", "LanSchool", "CIPA Compliance", "Structured Wiring"] :
                            expIdx === 2 ? ["Network Modernization", "High-Density Wi-Fi", "DNS Filtering", "Google Workspace", "Clever SSO"] :
                            expIdx === 3 ? ["$3M+ Fiber Project", "FTTH GPON", "Cisco Meraki", "Active Directory", "Windows Server"] :
                            expIdx === 4 ? ["Security Systems", "27-Cameras", "RMM Control", "Cost Optimization"] :
                            expIdx === 5 ? ["Responsive Design", "Modern UI/UX", "Engineering Services", "Frontend Stack"] :
                            ["Chromebook Hardware", "800+ Students", "Staff Support", "District IT Systems"];

                          return (
                            <motion.div
                              key={expIdx}
                              initial={{ opacity: 0, y: 15 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: expIdx * 0.05 }}
                              className="p-6 sm:p-7 bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/40 shadow-xl rounded-3xl relative overflow-hidden flex flex-col justify-between group transition-all duration-300 h-[460px] min-h-[460px]"
                            >
                              {/* Ambient Background Aura */}
                              <div className="absolute top-0 right-0 w-36 h-36 bg-brand-500/5 rounded-full blur-3xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                              {/* Card Header Slot */}
                              <div className="shrink-0 space-y-3">
                                {/* Header / Top Row */}
                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-900 pb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/10 text-brand-400 text-xs font-mono font-bold uppercase tracking-wider border border-brand-500/20">
                                      {exp.period}
                                    </span>
                                    {isFlagship && (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-bold uppercase tracking-wider border border-emerald-500/20">
                                        Municipal FTTH
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
                                    <MapPin className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                                    <span>{exp.location}</span>
                                  </div>
                                </div>

                                {/* Role & Company */}
                                <div>
                                  <h4 className="text-lg sm:text-xl font-display font-bold text-white group-hover:text-brand-300 transition-colors leading-tight line-clamp-1">
                                    {exp.role}
                                  </h4>
                                  <p className="text-xs sm:text-sm font-semibold text-brand-400 mt-1 truncate">
                                    {exp.company}
                                  </p>
                                </div>
                              </div>

                              {/* Achievements List Slot with uniform flex distribution */}
                              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 my-3 space-y-2">
                                <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block sticky top-0 bg-neutral-950/90 py-0.5">
                                  Key Accomplishments
                                </span>
                                <ul className="space-y-2">
                                  {exp.achievements.map((item, achIdx) => (
                                    <li
                                      key={achIdx}
                                      className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed bg-[#0c0c0c]/60 p-2.5 rounded-xl border border-neutral-900/60"
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Tech / Scope Tags Slot (Anchored at Bottom) */}
                              <div className="shrink-0 pt-3 border-t border-neutral-900/80 flex flex-wrap gap-1.5">
                                {tags.map((tag, tIdx) => (
                                  <span
                                    key={tIdx}
                                    className="px-2.5 py-1 bg-neutral-900/80 hover:bg-neutral-800 text-slate-400 hover:text-slate-200 text-[11px] rounded-lg border border-neutral-800 font-mono transition-colors"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Section 2: Professional Credentials & Certifications */}
                    <div className="space-y-6 pt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-neutral-900 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                            <GraduationCap className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                              Professional Credentials &amp; Certifications
                            </h3>
                            <p className="text-xs text-slate-400">Industry-recognized certifications in enterprise networking, systems, security, and digital design.</p>
                          </div>
                        </div>
                        <span className="text-xs font-mono font-bold text-brand-400 bg-brand-500/10 px-3 py-1 rounded-full border border-brand-500/20 self-start sm:self-auto">
                          {CERTIFICATIONS.length} Certifications
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CERTIFICATIONS.map((cert, k) => {
                          const isExpanded = !!expandedCerts[k];
                          return (
                            <div 
                              key={k} 
                              onClick={() => setExpandedCerts(prev => ({ ...prev, [k]: !prev[k] }))}
                              className={`p-5 rounded-2xl border transition-all duration-300 flex flex-col justify-between cursor-pointer relative select-none group ${
                                isExpanded 
                                  ? "border-brand-500 bg-[#0c0c0c] shadow-[0_0_25px_rgba(245,158,11,0.15)]"
                                  : "bg-neutral-950/90 border-brand-500/20 hover:border-brand-500/50 hover:shadow-[0_0_20px_rgba(245,158,11,0.08)]"
                              }`}
                            >
                              <div className="flex items-start gap-3.5">
                                <div className={`p-2.5 rounded-xl shrink-0 transition-colors ${isExpanded ? "bg-brand-500 text-white" : "bg-brand-500/10 text-brand-400 border border-brand-500/20"}`}>
                                  <GraduationCap className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-sm font-bold leading-snug transition-colors ${isExpanded ? "text-brand-400" : "text-white group-hover:text-brand-300"}`}>
                                    {cert.name}
                                  </h4>
                                  
                                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                                    {cert.desc}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bottom Resume Callout Banner */}
                    <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950/90 border border-brand-500/25 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
                      <div className="space-y-1">
                        <span className="text-[10px] font-mono font-bold text-brand-400 uppercase tracking-wider block">
                          Available for Direct Projects &amp; Consulting
                        </span>
                        <h4 className="text-xl sm:text-2xl font-display font-bold text-white">
                          Need Senior IT or Full-Stack Solutions?
                        </h4>
                        <p className="text-xs text-slate-400">
                          Direct consultation, enterprise network architecture, hardware diagnostics, or modern web applications.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
                        <button
                          onClick={() => {
                            setActiveTab("service-quote");
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-500 to-amber-500 hover:from-brand-400 hover:to-amber-400 text-neutral-950 font-mono font-bold text-xs uppercase tracking-wider transition-all active:scale-95 shadow-md cursor-pointer"
                        >
                          Request Service Quote
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab("contact");
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-5 py-2.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white border border-neutral-800 font-mono font-bold text-xs uppercase tracking-wider transition-all active:scale-95 cursor-pointer"
                        >
                          Get In Touch
                        </button>
                      </div>
                    </div>

                  </div>
                )}

                {/* ----------------- SERVICES TAB ----------------- */}
                {activeTab === "services" && (
                  <div className="space-y-10 sm:space-y-12 max-w-6xl mx-auto px-2 sm:px-4">
                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        <Wrench className="w-3.5 h-3.5" />
                        Professional Offerings
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">
                        Services &amp; Technical Solutions
                      </h2>
                      <p className="text-sm text-slate-400 max-w-2xl mx-auto">
                        Specialized capabilities across hardware diagnostics, enterprise systems integration, cloud administration, and infrastructure engineering.
                      </p>
                    </div>

                    {/* Core Technical Offerings in Two-Column Grid */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 border-b border-neutral-900 pb-4">
                        <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                          <Layers className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                            Core Technical Offerings
                          </h3>
                          <p className="text-xs text-slate-400">Specialized capabilities across hardware, infrastructure, cloud, and digital design.</p>
                        </div>
                      </div>

                      {/* Two-Column Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                        {[
                          { title: "Laptop & Desktop Solutions", category: "Hardware Support", desc: "Hardware diagnostics, component upgrades, complete system deployments, and seamless user profile migrations.", icon: <Monitor className="w-5 h-5 text-brand-400" />, tags: ["Component Upgrades", "OS Reinstalls", "Drive Cloning"] },
                          { title: "Hardware & Software Integration", category: "Systems Tuning", desc: "Comprehensive physical component repair, software troubleshooting, driver tuning, and operating system cleanups.", icon: <Cpu className="w-5 h-5 text-brand-400" />, tags: ["Driver Diagnostics", "Firmware", "Clean Installs"] },
                          { title: "Network Infrastructure Engineering", category: "Enterprise Networks", desc: "Enterprise wireless laydowns, secure VLAN segmentation, WAN routing, firewall integration, DNS filtering, IDS/IPS configurations, Geo-blocking, and active link troubleshooting.", icon: <Network className="w-5 h-5 text-brand-400" />, tags: ["VLAN Segmentation", "Cisco Meraki", "Ubiquiti UniFi"] },
                          { title: "Windows & Linux Server Deployment", category: "Server Systems", desc: "Active Directory & Azure/Entra ID setups, Google Workspace administration, virtualization, file servers, Proxmox hypervisors, containerized infrastructure, and database hosting.", icon: <Server className="w-5 h-5 text-brand-400" />, tags: ["Active Directory", "Proxmox", "File Sharing"] },
                          { title: "Mobile & Smart Device Support", category: "Endpoint Control", desc: "Smartphone/tablet repairs, firmware restorations, profile migrations, and enterprise MDM enrollment.", icon: <Layers className="w-5 h-5 text-brand-400" />, tags: ["Chromebooks", "MDM Setup", "Device Profiles"] },
                          { title: "Data Recovery & Integrity", category: "Storage & Safety", desc: "Intelligent file system restoration, high-precision partition rebuilding, storage drive migrations, and automated backups.", icon: <RefreshCw className="w-5 h-5 text-brand-400" />, tags: ["File Recovery", "Drive Migrations", "Automated Backups"] },
                          { title: "Websites (Traditional & AI Powered)", category: "Web Engineering", desc: "Easy-to-use custom websites that look great on any phone, tablet, or computer. Includes Google search setup, contact forms, and helpful modern features. Starting at $2,000.", icon: <Globe className="w-5 h-5 text-brand-400" />, tags: ["Mobile Friendly", "Search Optimized", "Fast & Modern"] },
                          { title: "Logo & Brand Identity Design", category: "Creative Branding", desc: "Custom vector logos, visual branding marks, modern typography hierarchy, iconography, and print/digital asset kits tailored for businesses and creators.", icon: <Palette className="w-5 h-5 text-brand-400" />, tags: ["Vector Logos", "Brand Books", "Digital Assets"] },
                          { title: "Structured Wiring & Cabling", category: "Physical Infrastructure", desc: "Structured CAT5e/6/7 copper terminations, server rack dressing, patch panel wiring, and signal performance certification.", icon: <Zap className="w-5 h-5 text-brand-400" />, tags: ["CAT6/7 Cabling", "Rack Dressing", "Patch Panels"] },
                          { title: "Surveillance & Access Security", category: "Physical Security", desc: "Commercial IP security cameras, NVR system setups, electronic access nodes, remote multi-site monitoring, and video audit storage.", icon: <Camera className="w-5 h-5 text-brand-400" />, tags: ["IP Cameras", "NVR Setup", "Access Nodes"] }
                        ].map((srv, sIdx) => (
                          <motion.div 
                            key={sIdx}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: sIdx * 0.04 }}
                            className="p-6 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/40 shadow-xl relative overflow-hidden flex flex-col justify-between group transition-all duration-300"
                          >
                            <div className="absolute top-0 right-0 w-36 h-36 bg-brand-500/5 rounded-full blur-3xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                            <div className="space-y-4">
                              <div className="flex items-center justify-between gap-2 border-b border-neutral-900 pb-3.5">
                                <div className="p-2.5 bg-brand-500/10 text-brand-400 rounded-xl shrink-0 group-hover:scale-105 transition-transform">
                                  {srv.icon}
                                </div>
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 text-slate-400 text-[10px] font-mono font-bold uppercase tracking-wider border border-neutral-800">
                                  {srv.category}
                                </span>
                              </div>

                              <div className="space-y-1.5">
                                <h4 className="font-display font-bold text-xl text-white group-hover:text-brand-300 transition-colors leading-tight">
                                  {srv.title}
                                </h4>
                                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed font-medium">
                                  {srv.desc}
                                </p>
                              </div>
                            </div>

                            <div className="pt-4 mt-4 border-t border-neutral-900/80 flex flex-wrap gap-1.5">
                              {srv.tags.map((tag, tIdx) => (
                                <span
                                  key={tIdx}
                                  className="px-2.5 py-1 bg-neutral-900/80 text-slate-400 text-[11px] rounded-lg border border-neutral-800 font-mono"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                  </div>
                )}

                {/* ----------------- PRICES TAB ----------------- */}
                {activeTab === "prices" && (
                  <PricingPage
                    onNavigateToQuote={(initialPrefill?: string, initialMode?: 'onsite' | 'pickup' | 'remote') => {
                      if (initialPrefill) {
                        setQuotePrefill(initialPrefill);
                      }
                      if (initialMode) {
                        setQuoteModePrefill(initialMode);
                      }
                      goToTab("service-quote");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onNavigateToWebOnboarding={() => {
                      goToTab("website-onboarding");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onNavigateToContact={() => {
                      goToTab("contact");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onNavigateToEstimator={() => {
                      goToTab("estimator");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onViewTerms={() => setIsTermsOpen(true)}
                  />
                )}

                {/* ----------------- PRICE ESTIMATOR TAB ----------------- */}
                {activeTab === "estimator" && (
                  <PriceEstimatorPage
                    onNavigateToQuote={(initialPrefill?: string, initialMode?: 'onsite' | 'pickup' | 'remote') => {
                      if (initialPrefill) {
                        setQuotePrefill(initialPrefill);
                      }
                      if (initialMode) {
                        setQuoteModePrefill(initialMode);
                      }
                      goToTab("service-quote");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onNavigateToPrices={() => {
                      goToTab("prices");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onViewTerms={() => setIsTermsOpen(true)}
                  />
                )}

                {/* ----------------- EXPERTISE TAB ----------------- */}
                {activeTab === "expertise" && (
                  <div className="space-y-10 sm:space-y-12 max-w-6xl mx-auto px-2 sm:px-4">
                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        Technical Expertise
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">Expertise and Technical Skills</h2>
                      <p className="text-sm text-slate-400">Proven proficiency across the entire modern IT stack, from physical cabling to cloud-based administration.</p>
                    </div>

                    {/* Two-Column Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                      {SKILLS.map((skillGroup, catIdx) => {
                        const isLastCard = catIdx === SKILLS.length - 1;
                        const getCategoryIcon = () => {
                          switch (catIdx) {
                            case 0: return <Network className="w-5 h-5 text-brand-400" />;
                            case 1: return <Cpu className="w-5 h-5 text-brand-400" />;
                            case 2: return <ShieldCheck className="w-5 h-5 text-brand-400" />;
                            case 3: return <Globe className="w-5 h-5 text-brand-400" />;
                            case 4: return <Camera className="w-5 h-5 text-brand-400" />;
                            default: return <Award className="w-5 h-5 text-brand-400" />;
                          }
                        };

                        const activeItemInThisGroup = skillGroup.items.find(item => item === activeSkillItem);

                        return (
                          <motion.div
                            key={catIdx}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: catIdx * 0.05 }}
                            className={`p-6 sm:p-7 bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/40 shadow-xl rounded-3xl relative overflow-hidden flex flex-col justify-between group transition-all duration-300 ${
                              isLastCard ? "md:col-span-2" : ""
                            }`}
                          >
                            {/* Ambient background aura */}
                            <div className="absolute top-0 right-0 w-36 h-36 bg-brand-500/5 rounded-full blur-3xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                            <div className="space-y-4">
                              {/* Category Header */}
                              <div className="flex items-center justify-between gap-2 border-b border-neutral-900/80 pb-3.5">
                                <div className="flex items-center gap-3">
                                  <div className="p-2.5 bg-brand-500/10 rounded-xl shrink-0">
                                    {getCategoryIcon()}
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500 block">
                                      Domain {catIdx + 1}
                                    </span>
                                    <h3 className="text-lg sm:text-xl font-display font-bold text-white tracking-tight">
                                      {skillGroup.category}
                                    </h3>
                                  </div>
                                </div>
                                <span className="text-[11px] font-mono text-slate-400 bg-neutral-900/80 px-2.5 py-1 rounded-full border border-neutral-800 shrink-0">
                                  {skillGroup.items.length} Skills
                                </span>
                              </div>

                              {/* Skill Tags */}
                              <div>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-2.5">
                                  Technical Skillsets
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {skillGroup.items.map((item, idx) => {
                                    const isSelected = activeSkillItem === item;
                                    return (
                                      <button
                                        key={idx}
                                        onMouseEnter={() => setActiveSkillItem(item)}
                                        onMouseLeave={() => setActiveSkillItem(null)}
                                        onClick={() => setActiveSkillItem(activeSkillItem === item ? null : item)}
                                        className={`px-2.5 py-1.5 border rounded-xl text-xs font-semibold shadow-sm transition-all duration-200 cursor-pointer select-none text-left focus:outline-none ${
                                          isSelected
                                            ? "border-brand-500 bg-brand-500 text-neutral-950 shadow-lg shadow-brand-500/20 font-bold"
                                            : "bg-[#0c0c0c] border-neutral-900 text-slate-300 hover:border-brand-500/40 hover:bg-[#111]"
                                        }`}
                                      >
                                        {item}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Detail / Description Drawer */}
                            <div className="pt-4 mt-5 border-t border-neutral-900/80 min-h-[56px] flex items-center">
                              {activeItemInThisGroup ? (
                                <div className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                                  <strong className="text-brand-400 mr-1.5">{activeItemInThisGroup}:</strong>
                                  {SKILL_DESCRIPTIONS[activeItemInThisGroup] || "Broad operational experience with this system configuration."}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 italic">
                                  Hover or tap any skill tag above to view operational background &amp; implementation.
                                </p>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ----------------- PROJECT SHOWCASE TAB ----------------- */}
                {activeTab === "portfolio" && (
                  <div className="space-y-10 sm:space-y-12 max-w-6xl mx-auto px-2 sm:px-4">
                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        Flagship Infrastructure
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">Project Showcase</h2>
                      <p className="text-sm text-slate-400">High-impact municipal broadband networks, multi-department civic overhauls, and district technology systems.</p>
                    </div>

                    {/* Two-Column Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                      {SHOWCASE_PROJECTS.map((site, projectIndex) => {
                        const isWideCard = projectIndex === 2;
                        return (
                          <motion.div
                            key={projectIndex}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: projectIndex * 0.08 }}
                            className={`p-6 sm:p-8 bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/40 shadow-xl rounded-3xl relative overflow-hidden flex flex-col justify-between group transition-all duration-300 ${
                              isWideCard ? "md:col-span-2" : ""
                            }`}
                          >
                            {/* Ambient Background Aura */}
                            <div className="absolute top-0 right-0 w-44 h-44 bg-brand-500/5 rounded-full blur-3xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                            <div className="space-y-5">
                              {/* Header & Badges */}
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="p-2.5 bg-brand-500/10 text-brand-400 rounded-xl shrink-0">
                                    <Award className="w-5 h-5" />
                                  </div>
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 text-[10px] font-mono font-bold uppercase tracking-wider select-none border border-brand-500/20">
                                    {site.category}
                                  </span>
                                </div>
                                <div className="flex gap-1 text-brand-400">
                                  {[...Array(5)].map((_, idx) => (
                                    <Zap key={idx} className="w-3.5 h-3.5 fill-current" />
                                  ))}
                                </div>
                              </div>

                              {/* Title & Description */}
                              <div>
                                <h3 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight leading-snug">
                                  {site.name}
                                </h3>
                                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mt-2.5">
                                  {site.description}
                                </p>
                              </div>

                              {/* Stats Grid */}
                              {site.stats && (
                                <div className="grid grid-cols-2 gap-3 pt-1">
                                  {site.stats.map((stat, idx) => (
                                    <div key={idx} className="p-3.5 rounded-2xl bg-[#0c0c0c] border border-neutral-900/80">
                                      <div className="text-xl sm:text-2xl font-display font-bold text-brand-400">{stat.value}</div>
                                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono font-bold mt-0.5">{stat.label}</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Key Links & Achievements */}
                              {site.links && (
                                <div className="pt-4 border-t border-neutral-900/80 space-y-2.5">
                                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold block">
                                    Key Highlights &amp; Deployments:
                                  </span>
                                  <div className={`space-y-2 ${isWideCard ? "grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 space-y-0" : ""}`}>
                                    {site.links.map((link, idx) => {
                                      const isActualLink = link.url && link.url !== "#";
                                      if (isActualLink) {
                                        return (
                                          <a
                                            key={idx}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-start gap-2 text-xs text-slate-300 hover:text-brand-400 transition-colors group/link p-2 rounded-xl bg-neutral-900/40 hover:bg-neutral-900/80 border border-neutral-900"
                                          >
                                            <CheckCircle className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                                            <span className="underline hover:no-underline flex-1 leading-relaxed">{link.text}</span>
                                            <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-60 group-hover/link:opacity-100 transition-opacity mt-0.5" />
                                          </a>
                                        );
                                      }
                                      return (
                                        <div
                                          key={idx}
                                          className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed p-1.5"
                                        >
                                          <CheckCircle className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                                          <span>{link.text}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ----------------- WEBSITES TAB ----------------- */}
                {activeTab === "websites" && (
                  <div className="space-y-10 sm:space-y-12">
                    {/* Top Onboarding Callout Button */}
                    <div className="flex justify-center pb-2 sm:pb-4">
                      <button
                        onClick={() => {
                          setActiveTab("website-onboarding");
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="group relative inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-brand-500/15 via-amber-500/20 to-brand-500/15 border border-brand-500/40 hover:border-brand-500 shadow-[0_0_25px_rgba(245,158,11,0.12)] hover:shadow-[0_0_35px_rgba(245,158,11,0.25)] text-white hover:text-brand-300 font-display font-bold text-sm sm:text-base transition-all duration-300 active:scale-98 cursor-pointer"
                      >
                        <Sparkles className="w-4 h-4 text-brand-400 group-hover:rotate-12 transition-transform" />
                        <span>Need a website, or ready to onboard?</span>
                        <span className="inline-flex items-center gap-1 text-xs font-mono font-bold text-brand-400 bg-brand-500/20 px-2.5 py-1 rounded-full border border-brand-500/30 group-hover:bg-brand-500 group-hover:text-neutral-950 transition-colors">
                          Setup Guide &rarr;
                        </span>
                      </button>
                    </div>

                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        <Globe className="w-3.5 h-3.5" />
                        Custom Websites &amp; Portals
                      </div>
                      <h2 className="text-3xl font-display font-bold text-white">Websites</h2>
                      <p className="text-sm text-slate-400">
                        Websites built and/or cared for by Randon Brown. Clean, fast, and easy-to-navigate websites for local businesses, schools, and communities — <strong className="text-white font-semibold">starting at $2,000</strong>.
                      </p>
                    </div>

                    {/* Responsive Grid of Website Showcase Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                      {WEBSITE_PROJECTS.map((site, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.04, duration: 0.3 }}
                          className="group relative p-6 sm:p-8 rounded-2xl bg-neutral-950/80 hover:bg-neutral-900/90 border border-brand-500/20 hover:border-brand-500/50 shadow-[0_0_30px_rgba(245,158,11,0.04)] hover:shadow-[0_0_40px_rgba(245,158,11,0.12)] transition-all duration-300 flex flex-col justify-between text-left"
                        >
                          {/* Ambient Corner Glow */}
                          <div className="absolute top-0 right-0 w-28 h-28 bg-brand-500/5 rounded-full blur-2xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                          <div>
                            {/* Top row: Category Badge & Status */}
                            <div className="flex items-center justify-between gap-3 mb-4">
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/10 text-brand-400 text-xs font-mono font-bold uppercase tracking-wider border border-brand-500/20">
                                <Globe className="w-3.5 h-3.5" />
                                {site.category}
                              </span>
                              {site.availableSoon && (
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-semibold">
                                  Upcoming
                                </span>
                              )}
                            </div>

                            {/* Site Name */}
                            <h3 className="text-xl sm:text-2xl font-display font-bold text-white group-hover:text-brand-300 transition-colors mb-2.5">
                              {site.name}
                            </h3>

                            {/* Description */}
                            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-6 font-sans">
                              {site.description}
                            </p>
                          </div>

                          {/* Bottom Action Area */}
                          <div className="pt-4 border-t border-neutral-900/80 flex items-center justify-end min-h-[3.25rem]">
                            {site.availableSoon ? (
                              <span className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-wider">
                                Launching Q4 2026
                              </span>
                            ) : site.url ? (
                              <a
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-5 py-2.5 bg-brand-500/10 hover:bg-brand-500 text-brand-400 hover:text-neutral-950 border border-brand-500/30 hover:border-brand-500 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all shadow-sm active:scale-95 cursor-pointer"
                              >
                                <span>Visit Website</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            ) : (
                              <span className="text-[11px] font-mono text-slate-500 font-medium">
                                Regional Organization
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ----------------- BOOKS TAB ----------------- */}
                {activeTab === "books" && (
                  <BooksShowcase onContactClick={() => goToTab("contact")} />
                )}

                {/* ----------------- WEBSITE ONBOARDING GUIDE TAB ----------------- */}
                {activeTab === "website-onboarding" && (
                  <WebsiteOnboardingGuide 
                    onBack={() => {
                      setActiveTab("websites");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }} 
                    onViewTerms={() => setIsTermsOpen(true)}
                  />
                )}

                {/* ----------------- SERVICE / REPAIR QUOTE REQUEST TAB ----------------- */}
                {activeTab === "service-quote" && (
                  <ServiceQuoteRequest 
                    onBack={() => {
                      setActiveTab("services");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }} 
                    onViewTerms={() => setIsTermsOpen(true)}
                    initialProblem={quotePrefill}
                    initialServiceMode={quoteModePrefill}
                  />
                )}

                {/* ----------------- TERMS & CONDITIONS TAB ----------------- */}
                {activeTab === "terms" && (
                  <TermsAndConditions onBack={() => {
                    setActiveTab("services");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} />
                )}

                {/* ----------------- CLIENT PORTAL TAB ----------------- */}
                {activeTab === "portal" && (
                  <ClientPortal onBack={() => {
                    setActiveTab("home");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} />
                )}

                {/* ----------------- ADMIN DASHBOARD TAB ----------------- */}
                {activeTab === "admin" && (
                  <AdminDashboard onBack={() => {
                    setActiveTab("home");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} />
                )}

                {/* ----------------- AI APPS TAB ----------------- */}
                {activeTab === "apps" && (
                  <div className="space-y-10 max-w-6xl mx-auto">
                    {/* Centered Header Block */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        Built with Google AI Studio
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">AI-Powered Web Apps</h2>
                      <p className="text-sm text-slate-400">Leveraging cutting-edge language models to solve daily problems through sleek web portals.</p>
                    </div>

                    {/* Two-Column Grid of AI Applications */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                      {AI_APPS.map((app, index) => (
                        <motion.div
                          key={app.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className="group relative flex flex-col justify-between p-7 sm:p-8 bg-neutral-950/90 border border-brand-500/20 hover:border-brand-500/50 shadow-[0_0_30px_rgba(245,158,11,0.05)] hover:shadow-[0_0_50px_rgba(245,158,11,0.15)] transition-all duration-300 rounded-3xl overflow-hidden"
                        >
                          {/* Ambient Background Aura */}
                          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/5 rounded-full blur-2xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />
                          <div className="absolute bottom-0 left-0 w-32 h-32 bg-brand-500/5 rounded-full blur-2xl pointer-events-none group-hover:bg-brand-500/10 transition-colors" />

                          <div>
                            {/* Top row: App Icon & Star Rating Accent */}
                            <div className="flex items-center justify-between mb-5">
                              <div className="p-3.5 bg-brand-500/10 text-brand-400 rounded-2xl group-hover:scale-105 group-hover:bg-brand-500/20 transition-all shadow-inner">
                                {app.icon}
                              </div>
                              <div className="flex gap-1 text-brand-400">
                                {[...Array(5)].map((_, idx) => (
                                  <Zap key={idx} className="w-3.5 h-3.5 fill-current animate-gold-pulse" />
                                ))}
                              </div>
                            </div>

                            {/* App Title */}
                            <h3 className="text-xl sm:text-2xl font-display font-bold text-white mb-2 group-hover:text-brand-300 transition-colors">
                              {app.name}
                            </h3>

                            {/* Mobile Friendly Web First Badge */}
                            <div className="mb-3.5 inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-500/10 text-brand-400 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full border border-brand-500/20">
                              <span>Mobile Friendly "Web first approach"</span>
                            </div>

                            {/* App Description */}
                            <p className="text-sm text-slate-300 leading-relaxed font-sans mb-5">
                              {app.description}
                            </p>

                            {/* Tech Feature Badges */}
                            <div className="flex flex-wrap gap-2 mb-6">
                              {app.features.map((feature, j) => (
                                <span
                                  key={j}
                                  className="px-2.5 py-1 bg-neutral-900 text-slate-300 text-[11px] font-bold uppercase tracking-wider rounded-lg border border-neutral-800"
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Action Launch Button */}
                          <div className="pt-4 border-t border-neutral-900/80 flex items-center justify-between mt-auto">
                            <span className="text-[11px] font-mono text-slate-500">Live Web Application</span>
                            <a
                              href={app.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-5 py-2.5 bg-[#0c0c0c] text-brand-400 border border-neutral-800 hover:border-brand-500/40 rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-neutral-900 cursor-pointer shadow-md active:scale-95 transition-all"
                            >
                              Launch Application <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ----------------- CONTACT TAB ----------------- */}
                {activeTab === "contact" && (
                  <div className="space-y-12 max-w-5xl mx-auto">
                    {/* Centered Header Block */}
                    <div className="text-center space-y-3">
                      <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider select-none">
                        Get In Touch
                      </div>
                      <h2 className="text-3xl font-display font-bold text-white">Let's Connect</h2>
                      <p className="text-sm text-slate-400 max-w-xl mx-auto">
                        Ready to start something great? Reach out today.
                      </p>
                    </div>

                    {/* Symmetrical 4-Column Direct Channels Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      
                      {/* Email Card */}
                      <div className="p-6 bg-neutral-950 rounded-2xl border border-brand-500/20 flex flex-col items-center text-center justify-between min-h-[220px] transition-all duration-300 hover:border-brand-500/50 hover:shadow-[0_0_35px_rgba(245,158,11,0.12)] group">
                        <div className="space-y-4 flex flex-col items-center">
                          <div className="p-3 bg-brand-500/10 text-brand-400 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                            <Mail className="w-6 h-6" />
                          </div>
                          <div>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Email Address</span>
                            <span className="text-sm font-bold text-slate-200 select-all">
                              {revealedEmail ? "help@randonbrown.com" : "help•••••@randonbrown.com"}
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={(e) => {
                            if (!revealedEmail) {
                              setRevealedEmail(true);
                            } else {
                              handleEmailClick(e);
                            }
                          }}
                          className="w-full mt-6 py-2.5 bg-[#0c0c0c] hover:bg-neutral-900 text-brand-400 hover:text-brand-300 border border-brand-500/20 hover:border-brand-500/40 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer select-none shadow-sm active:scale-95"
                        >
                          {revealedEmail ? "Send Email" : "Reveal Email"} <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Phone Card */}
                      <div className="p-6 bg-neutral-950 rounded-2xl border border-brand-500/20 flex flex-col items-center text-center justify-between min-h-[220px] transition-all duration-300 hover:border-brand-500/50 hover:shadow-[0_0_35px_rgba(245,158,11,0.12)] group">
                        <div className="space-y-4 flex flex-col items-center">
                          <div className="p-3 bg-brand-500/10 text-brand-400 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                            <Phone className="w-6 h-6" />
                          </div>
                          <div>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Phone Line</span>
                            <span className="text-sm font-bold text-slate-200 select-all">
                              {revealedPhone ? "(314) 944-3074" : "(314) •••-••••"}
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={(e) => {
                            if (!revealedPhone) {
                              setRevealedPhone(true);
                            } else {
                              handlePhoneClick(e);
                            }
                          }}
                          className="w-full mt-6 py-2.5 bg-[#0c0c0c] hover:bg-neutral-900 text-brand-400 hover:text-brand-300 border border-brand-500/20 hover:border-brand-500/40 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer select-none shadow-sm active:scale-95"
                        >
                          {revealedPhone ? "Call Now" : "Reveal Phone"} <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* LinkedIn Card */}
                      <div className="p-6 bg-neutral-950 rounded-2xl border border-brand-500/20 flex flex-col items-center text-center justify-between min-h-[220px] transition-all duration-300 hover:border-brand-500/50 hover:shadow-[0_0_35px_rgba(245,158,11,0.12)] group">
                        <div className="space-y-4 flex flex-col items-center">
                          <div className="p-3 bg-brand-500/10 text-brand-400 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                            <LinkedInIcon className="w-6 h-6" />
                          </div>
                          <div>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">LinkedIn</span>
                            <span className="text-sm font-bold text-slate-200">Connect Collaboratively</span>
                          </div>
                        </div>
                        <a 
                          href="https://www.linkedin.com/in/randon-brown-7139ab127" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          aria-label="Connect with Randon Brown on LinkedIn"
                          className="w-full mt-6 py-2.5 bg-[#0c0c0c] hover:bg-neutral-900 text-brand-400 hover:text-brand-300 border border-brand-500/20 hover:border-brand-500/40 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer select-none shadow-sm active:scale-95"
                        >
                          Connect <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>

                    </div>

                    {/* Elegant Geographic Base Banner */}
                    <div className="p-6 md:p-8 rounded-2xl bg-neutral-950 border border-brand-500/20 hover:border-brand-500/50 hover:shadow-[0_0_35px_rgba(245,158,11,0.12)] shadow-[0_0_30px_rgba(245,158,11,0.03)] transition-all duration-300 flex flex-col md:flex-row items-center justify-between gap-6 max-w-4xl mx-auto">
                      <div className="flex items-start gap-4 text-left">
                        <div className="p-3 bg-brand-500/10 text-brand-400 rounded-xl shrink-0 mt-1">
                          <MapPin className="w-6 h-6 animate-gold-pulse" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-white uppercase tracking-wider font-mono">Located in</h4>
                          <p className="text-sm text-slate-300 mt-1">Licking, Missouri, United States</p>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            Serving Texas County, Missouri and beyond!
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 text-center md:text-right bg-[#0c0c0c] px-5 py-4 rounded-xl border border-brand-500/20">
                        <span className="text-[10px] font-mono font-bold text-slate-500 block uppercase tracking-wider">Direct Availability</span>
                        <span className="text-xs font-bold text-brand-400 block mt-1">Typically responds within 2 hours</span>
                      </div>
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>

          {/* Universal Bottom Call to Action for Non-Home Pages */}
          {activeTab !== "home" && activeTab !== "admin" && (
            <BottomCallToAction 
              onSelectTab={(tabId) => {
                setActiveTab(tabId);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} 
            />
          )}

          {/* Bottom Site Footer */}
          <footer className="mt-auto px-4 sm:px-8 pt-6 pb-20 sm:pb-6 border-t border-neutral-900 bg-neutral-950/60 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-500 select-none relative">
            <div className="flex items-center gap-2 text-center sm:text-left shrink-0">
              <BrandLogo size="sm" className="w-4 h-4 opacity-70" />
              <span>&copy; {new Date().getFullYear()} Randon Brown, LLC. All Rights Reserved.</span>
            </div>

            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-3 gap-y-2 text-slate-400 text-[11px] sm:pr-14 md:pr-16 shrink-0">
              <button 
                onClick={() => setIsTermsOpen(true)}
                className="hover:text-brand-400 transition-colors cursor-pointer"
              >
                Terms & Conditions
              </button>
              <span className="text-neutral-800">&bull;</span>
              <button 
                onClick={() => setIsPrivacyOpen(true)}
                className="hover:text-brand-400 transition-colors cursor-pointer"
              >
                Privacy
              </button>
              <span className="text-neutral-800">&bull;</span>

              {/* Admin Login / Admin Portal (Active when logged in, subtle/greyed otherwise) */}
              {currentAdminUser ? (
                <button 
                  onClick={() => {
                    setActiveTab("admin");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer flex items-center gap-1.5 font-medium"
                  title="Owner Database Portal (Active)"
                >
                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                  <span>Admin Portal</span>
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setActiveTab("admin");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-neutral-600 hover:text-neutral-400 transition-colors cursor-pointer flex items-center gap-1 opacity-60 hover:opacity-100"
                  title="Owner Administrative Sign-In"
                >
                  <Lock className="w-2.5 h-2.5" />
                  <span>Admin Login</span>
                </button>
              )}
            </div>
          </footer>

          {/* Floating Scroll to Top Button */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                transition={{ duration: 0.2 }}
                onClick={scrollToTop}
                className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-40 h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-full bg-neutral-900/95 backdrop-blur-md border border-brand-500/30 hover:border-brand-500 text-brand-400 hover:text-brand-300 shadow-xl hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] active:scale-95 transition-all flex items-center justify-center cursor-pointer group"
                title="Scroll to Top"
              >
                <ArrowUp className="w-5 h-5 md:w-6 md:h-6 group-hover:-translate-y-1 transition-transform duration-300" />
              </motion.button>
            )}
          </AnimatePresence>
        </main>
      </div>
        </div>

      {/* ----------------- DIALOG MODALS ----------------- */}

      {/* API Info / Settings Modal */}
      <AnimatePresence>
        {isSettingsOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
              onClick={() => setIsSettingsOpen(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-sm bg-[#0c0c0c] border border-neutral-900 rounded-2xl overflow-hidden flex flex-col shadow-2xl z-10 text-slate-200"
            >
              <div className="p-5 border-b border-neutral-900 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-brand-400" />
                  <span className="font-bold text-sm text-white">Service Providers</span>
                </div>
                <button 
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-1.5 hover:bg-neutral-900 rounded-full transition-colors text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {[
                  { name: "Google Fonts API", desc: "Premium Plus Jakarta Sans & Playfair typography streams." },
                  { name: "Lucide Vector Suite", desc: "Clean vector elements and high-definition layouts." },
                  { name: "Resend Email API", desc: "Automated transactional notifications and client receipts." },
                  { name: "Google Cloud Firestore", desc: "Encrypted persistence for intake forms and quotes." }
                ].map((api, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="text-xs font-bold text-brand-400">{api.name}</div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{api.desc}</p>
                  </div>
                ))}
              </div>
              <div className="p-5 bg-neutral-950/60 border-t border-neutral-900 flex flex-col items-center gap-3">
                <span className="text-[9px] font-mono text-slate-600">ENVIRONMENT: PRODUCTION | STATUS: ACTIVE</span>
                <button 
                  onClick={() => setIsSettingsOpen(false)}
                  className="w-full py-2 bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Privacy Policy Pop-Out Modal */}
      <PrivacyPolicyModal 
        isOpen={isPrivacyOpen}
        onClose={() => setIsPrivacyOpen(false)}
      />

      {/* Testimonials Recommendation Letter Modal */}
      <AnimatePresence>
        {selectedLetter && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
              onClick={() => setSelectedLetter(null)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-lg bg-[#0c0c0c] border border-neutral-900 rounded-2xl overflow-hidden flex flex-col shadow-2xl z-10 text-slate-300 max-h-[80vh]"
            >
              <div className="p-5 border-b border-neutral-900 flex justify-between items-start shrink-0">
                <div>
                  <h3 className="font-display font-bold text-base text-white">{selectedLetter.name}</h3>
                  <p className="text-[11px] text-brand-400 mt-0.5">{selectedLetter.role} &bull; {selectedLetter.org}</p>
                </div>
                <button 
                  onClick={() => setSelectedLetter(null)}
                  className="p-1.5 hover:bg-neutral-900 rounded-full transition-colors text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar text-xs md:text-sm text-slate-300 leading-relaxed italic font-serif">
                <div className="w-8 h-1 bg-brand-500 rounded-full mb-4 animate-gold-pulse" />
                "{selectedLetter.full}"
              </div>

              <div className="p-4 bg-neutral-950/60 border-t border-neutral-900 text-right shrink-0">
                <button 
                  onClick={() => setSelectedLetter(null)}
                  className="px-5 py-2 bg-brand-500 hover:bg-brand-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                >
                  Close Letter
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Spotlight Site Search Modal */}
      <AnimatePresence>
        {isSearchOpen && (
          <SiteSearch 
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            onSelectTab={goToTab}
            setSelectedSkillIndex={setSelectedSkillIndex}
            setActiveSkillItem={setActiveSkillItem}
            setExpandedCerts={setExpandedCerts}
            setSelectedWebIndex={setSelectedWebIndex}
            setSelectedRecIndex={setSelectedRecIndex}
            onOpenPrivacy={() => setIsPrivacyOpen(true)}
            onOpenCookiePolicy={() => setIsCookiePolicyOpen(true)}
            skills={SKILLS}
            skillDescriptions={SKILL_DESCRIPTIONS}
            certifications={CERTIFICATIONS}
            aiApps={AI_APPS}
            portfolioProjects={PORTFOLIO_PROJECTS}
            recommendations={RECOMMENDATIONS}
          />
        )}
      </AnimatePresence>

      {/* Global Cookie Policy Modal */}
      <CookiePolicyModal
        isOpen={isCookiePolicyOpen}
        onClose={() => setIsCookiePolicyOpen(false)}
        consent={cookieConsent.consent}
        onResetPreferences={cookieConsent.resetPreferences}
        onAcceptAll={cookieConsent.acceptAll}
        onAcceptEssential={cookieConsent.acceptEssential}
      />

      {/* Terms & Conditions Pop-Out Panel */}
      <TermsAndConditionsModal
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
      />

      {/* Full Page Navigation Hub Roadmap */}
      <NavigationHub
        isOpen={isNavHubOpen}
        onClose={() => setIsNavHubOpen(false)}
        onSelectTab={(tabId) => goToTab(tabId)}
        currentTab={activeTab}
      />

    </div>
    </>
  );
}
